<template>
  <div>
    <div class="v-card portfolio-summary__root-card">
      <v-progress-linear
        v-if="isLoading"
        absolute
        top
        indeterminate
        color="primary"
      />
      <v-card-title class="align-start portfolio-summary__header">
        <div class="portfolio-summary__header-copy">
          <h3 class="card-label mb-1">Portföy Özeti</h3>
          <div class="text-muted">
            Bugün itibarıyla müşteri portföyünüzün genel durumu, riskleri ve
            aksiyon gerektiren alanlar
          </div>
          <div
            class="portfolio-summary__context-chips d-flex flex-wrap align-center mt-2"
          >
            <span class="portfolio-summary__context-chip mr-2 mb-1">
              Referans tarihi:
              <strong>{{ formatDate(referenceDate) }}</strong>
            </span>
            <span class="portfolio-summary__context-chip mr-2 mb-1">
              Aktif dönem:
              <strong>{{ currentBillingPeriodLabel }}</strong>
            </span>
            <span class="portfolio-summary__context-chip mr-2 mb-1">
              Son yenileme:
              <strong>{{ lastLoadedAtLabel }}</strong>
            </span>
            <span class="portfolio-summary__context-chip mr-2 mb-1">
              Sıradaki otomatik kesim:
              <strong>{{ nextAutomaticBillingRunLabel }}</strong>
            </span>
          </div>
        </div>

        <v-spacer />

        <div class="portfolio-summary__header-actions">
          <v-btn
            small
            color="primary"
            class="mr-2 mb-2"
            @click="yearEndTransitionDialogOpen = true"
          >
            <v-icon left small>mdi-calendar-refresh</v-icon>
            Yenileme Aracı
          </v-btn>
          <v-btn small outlined color="primary" class="mr-2 mb-2" @click="load">
            <v-icon left small>mdi-refresh</v-icon>
            Yenile
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <div class="portfolio-summary__kpi-section mb-4">
          <div
            class="portfolio-summary__kpi-section-header d-flex flex-wrap align-center justify-space-between"
          >
            <div class="portfolio-summary__kpi-section-title">
              Portföy kapsamı
            </div>
            <div class="portfolio-summary__kpi-section-caption">
              Risk ve gecikme detayları aşağıdaki tablarda yönetilir.
            </div>
          </div>

          <v-row dense>
            <v-col
              v-for="card in kpiCards"
              :key="card.key"
              class="d-flex"
              cols="12"
              sm="6"
              md="3"
            >
              <v-sheet
                outlined
                rounded
                class="portfolio-summary__metric d-flex flex-column"
                :class="[
                  `portfolio-summary__metric--${card.variant || 'standard'}`,
                  card.tone && `portfolio-summary__metric--${card.tone}`,
                  card.actionable && 'portfolio-summary__metric--clickable',
                  isCardActive(card) && 'portfolio-summary__metric--active',
                ]"
                :role="card.actionable ? 'button' : undefined"
                :tabindex="card.actionable ? 0 : undefined"
                @click="handleCardClick(card)"
                @keydown.enter.prevent="handleCardClick(card)"
                @keydown.space.prevent="handleCardClick(card)"
              >
                <div class="portfolio-summary__metric-topline">
                  <div class="portfolio-summary__metric-label">
                    {{ card.label }}
                  </div>
                  <div
                    v-if="card.icon || card.iconText"
                    class="portfolio-summary__metric-icon"
                    :class="
                      card.iconTone &&
                      `portfolio-summary__metric-icon--${card.iconTone}`
                    "
                  >
                    <span v-if="card.iconText">
                      {{ card.iconText }}
                    </span>
                    <v-icon v-else small :color="card.iconColor || 'primary'">
                      {{ card.icon }}
                    </v-icon>
                  </div>
                </div>
                <template v-if="card.variant === 'invoice-flow'">
                  <div class="portfolio-summary__split-metric">
                    <div class="portfolio-summary__split-metric-item">
                      <div class="portfolio-summary__metric-flow-value">
                        {{ formatKpiCurrency(card.invoicedValue) }}
                      </div>
                      <div class="portfolio-summary__metric-flow-label">
                        Faturalanan
                      </div>
                    </div>
                    <div class="portfolio-summary__split-metric-item">
                      <div
                        class="portfolio-summary__metric-flow-value portfolio-summary__metric-flow-value--success"
                      >
                        {{ formatKpiCurrency(card.collectedValue) }}
                      </div>
                      <div class="portfolio-summary__metric-flow-label">
                        Tahsil Edilen
                      </div>
                    </div>
                  </div>
                  <div class="portfolio-summary__metric-flow-progress">
                    <span
                      :style="{
                        width: `${getInvoiceFlowCollectionRate(card)}%`,
                      }"
                    />
                  </div>
                  <div class="portfolio-summary__metric-flow-note">
                    <span>Tahsilat oranı</span>
                    <strong>
                      %{{ formatInteger(getInvoiceFlowCollectionRate(card)) }}
                    </strong>
                  </div>
                </template>
                <template v-else>
                  <div class="portfolio-summary__metric-value">
                    {{ formatInteger(card.value) }}
                  </div>
                  <div class="portfolio-summary__metric-caption">
                    {{ card.caption }}
                  </div>
                  <div
                    v-if="card.breakdown && card.breakdown.length"
                    class="portfolio-summary__metric-breakdown"
                  >
                    <span
                      v-for="breakdown in card.breakdown"
                      :key="breakdown.label"
                      class="portfolio-summary__metric-breakdown-chip"
                    >
                      {{ breakdown.label }}
                      <strong>{{ formatInteger(breakdown.value) }}</strong>
                    </span>
                  </div>
                </template>
                <div v-if="card.tooltip" class="portfolio-summary__metric-help">
                  <v-tooltip bottom max-width="280">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <v-icon small color="grey lighten-1">
                          mdi-information-outline
                        </v-icon>
                      </span>
                    </template>
                    <span>{{ card.tooltip }}</span>
                  </v-tooltip>
                </div>
              </v-sheet>
            </v-col>
          </v-row>
        </div>

        <v-card outlined class="mb-4 portfolio-summary__operation-strip">
          <div
            class="portfolio-summary__operation-strip-header d-flex flex-wrap align-center justify-space-between"
          >
            <div>
              <div class="portfolio-summary__operation-strip-title">
                Bugünkü Operasyon Özeti
              </div>
              <div class="text-caption text--secondary">
                Önce faturalamayı bloklayan ve bugün aksiyon bekleyen kayıtlar
                gösterilir.
              </div>
            </div>

            <v-btn
              small
              text
              color="primary"
              class="mt-2 mt-sm-0"
              @click="clearOperationSummaryView"
            >
              Görünümü Temizle
            </v-btn>
          </div>

          <div class="portfolio-summary__operation-items">
            <button
              v-for="item in operationSummaryItems"
              :key="item.key"
              type="button"
              class="portfolio-summary__operation-item"
              :class="[
                `portfolio-summary__operation-item--${item.tone}`,
                isOperationSummaryItemActive(item) &&
                  'portfolio-summary__operation-item--active',
              ]"
              @click="handleOperationSummaryClick(item)"
            >
              <span class="portfolio-summary__operation-icon">
                <v-icon small>{{ item.icon }}</v-icon>
              </span>
              <span class="portfolio-summary__operation-copy">
                <span class="portfolio-summary__operation-title">
                  {{ item.title }}
                </span>
                <span class="portfolio-summary__operation-caption">
                  {{ item.caption }}
                </span>
                <span class="portfolio-summary__operation-link">
                  {{ item.actionLabel }}
                </span>
              </span>
              <span class="portfolio-summary__operation-count">
                {{ formatInteger(item.count) }}
              </span>
            </button>
          </div>
        </v-card>

        <v-card outlined>
          <v-tabs
            v-model="selectedTab"
            background-color="transparent"
            class="portfolio-summary__tabs"
          >
            <v-tab>
              <v-badge
                class="portfolio-summary__tab-badge"
                color="transparent"
                :content="formatInteger(kpis.overdue_customer_count)"
              >
                Geciken Ödemeler
              </v-badge>
            </v-tab>
            <v-tab>
              <v-badge
                class="portfolio-summary__tab-badge"
                color="transparent"
                :content="formatInteger(meta.risk_total)"
              >
                Riskli Kayıtlar
              </v-badge>
            </v-tab>
            <v-tab>
              <v-badge
                class="portfolio-summary__tab-badge"
                color="transparent"
                :content="formatInteger(billingTabBadgeCount)"
              >
                Faturalandırma
              </v-badge>
            </v-tab>
            <v-tab>
              <v-badge
                class="portfolio-summary__tab-badge"
                color="transparent"
                :content="formatInteger(meta.monthly_total)"
              >
                Aylık Müşteriler
              </v-badge>
            </v-tab>
            <v-tab>
              <v-badge
                class="portfolio-summary__tab-badge"
                color="transparent"
                :content="formatInteger(meta.yearly_total)"
              >
                Yıllık Müşteriler
              </v-badge>
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="selectedTab" touchless>
            <v-tab-item class="pa-4">
              <div class="portfolio-summary__table-toolbar">
                <div class="portfolio-summary__table-toolbar-copy">
                  <div class="portfolio-summary__table-toolbar-title">
                    15 günü geçen açık faturalar
                  </div>
                  <div class="portfolio-summary__table-toolbar-subtitle">
                    Öncelik: en eski açık fatura ve en yüksek geciken tutar.
                  </div>
                </div>

                <div class="portfolio-summary__table-toolbar-filters">
                  <span
                    class="portfolio-summary__table-filter-chip portfolio-summary__table-filter-chip--active"
                  >
                    Tümü
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(overdueToolbarStats.total) }}
                    </span>
                  </span>
                  <span class="portfolio-summary__table-filter-chip">
                    30+ gün
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(overdueToolbarStats.over30) }}
                    </span>
                  </span>
                  <span class="portfolio-summary__table-filter-chip">
                    Yıllık
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(overdueToolbarStats.yearly) }}
                    </span>
                  </span>

                  <v-btn
                    v-if="
                      priorityFilterType && priorityFilterType !== 'overdue'
                    "
                    small
                    text
                    color="primary"
                    @click="clearPriorityDrilldown"
                  >
                    Görünümü Temizle
                  </v-btn>
                </div>
              </div>

              <v-data-table
                class="portfolio-summary__data-table"
                :headers="priorityHeaders"
                :items="priorityActions"
                :loading="isLoading"
                :server-items-length="meta.priority_total"
                :items-per-page="priorityItemsPerPage"
                :page="priorityPage"
                :footer-props="footerProps"
                dense
                mobile-breakpoint="0"
                :no-data-text="priorityNoDataText"
                @update:page="handlePriorityPageUpdated"
                @update:items-per-page="handlePriorityItemsPerPageUpdated"
              >
                <!-- eslint-disable-next-line -->
                <template v-slot:item.customer_scope="{ item }">
                  <div class="portfolio-summary__table-entity">
                    <div class="portfolio-summary__table-entity-primary">
                      {{ item.customer_name || "-" }}
                    </div>
                    <div class="portfolio-summary__table-entity-secondary">
                      {{ item.cluster_label || "-" }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.billing_type="{ value }">
                  <v-chip small outlined :color="getBillingTypeColor(value)">
                    {{ value || "-" }}
                  </v-chip>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.critical_date="{ value }">
                  {{ formatDate(value) }}
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.overdue_summary="{ item }">
                  <div class="portfolio-summary__table-stack">
                    <div class="portfolio-summary__table-stack-primary">
                      {{ formatInteger(item.overdue_invoice_count) }} fatura
                    </div>
                    <div class="portfolio-summary__table-stack-secondary">
                      {{ formatCurrency(item.overdue_amount) }} gecikmiş
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.collection_risk="{ item }">
                  <v-chip
                    small
                    outlined
                    :color="getOverdueCollectionRiskColor(item)"
                  >
                    {{ getOverdueCollectionRiskLabel(item) }}
                  </v-chip>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.action="{ item }">
                  <div class="d-flex align-center justify-center">
                    <rs-row-actions-menu
                      :actions="getPriorityMenuActions(item)"
                      @select="handleRowActionSelect"
                    />
                  </div>
                </template>
              </v-data-table>
            </v-tab-item>

            <v-tab-item class="pa-4">
              <div class="portfolio-summary__table-toolbar">
                <div class="portfolio-summary__table-toolbar-copy">
                  <div class="portfolio-summary__table-toolbar-title">
                    Faturalamayı bloklayan riskler
                  </div>
                  <div class="portfolio-summary__table-toolbar-subtitle">
                    Önce kritik kurulum sorunlarını çöz, sonra iletişim ve
                    profil eksiklerini tamamla.
                  </div>
                </div>

                <div class="portfolio-summary__table-toolbar-filters">
                  <span
                    class="portfolio-summary__table-filter-chip"
                    :class="{
                      'portfolio-summary__table-filter-chip--active':
                        !riskFilterType,
                    }"
                  >
                    Tümü
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(riskToolbarStats.total) }}
                    </span>
                  </span>
                  <span class="portfolio-summary__table-filter-chip">
                    Kritik
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(riskToolbarStats.critical) }}
                    </span>
                  </span>
                  <span class="portfolio-summary__table-filter-chip">
                    Eksik ürün
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(riskToolbarStats.missingProduct) }}
                    </span>
                  </span>
                  <span class="portfolio-summary__table-filter-chip">
                    Yetkili yok
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{
                        formatInteger(riskToolbarStats.missingAuthorizedUser)
                      }}
                    </span>
                  </span>

                  <v-btn
                    v-if="riskFilterType"
                    small
                    text
                    color="primary"
                    @click="clearRiskDrilldown"
                  >
                    Görünümü Temizle
                  </v-btn>
                </div>
              </div>

              <v-data-table
                class="portfolio-summary__data-table"
                :headers="riskHeaders"
                :items="risks"
                :loading="isLoading"
                :server-items-length="meta.risk_total"
                :items-per-page="riskItemsPerPage"
                :page="riskPage"
                :footer-props="footerProps"
                dense
                mobile-breakpoint="0"
                :no-data-text="riskNoDataText"
                :item-class="getRiskRowClass"
                @update:page="handleRiskPageUpdated"
                @update:items-per-page="handleRiskItemsPerPageUpdated"
              >
                <!-- eslint-disable-next-line -->
                <template v-slot:item.customer_scope="{ item }">
                  <div class="portfolio-summary__table-entity">
                    <div class="portfolio-summary__table-entity-primary">
                      {{ item.customer_name || "-" }}
                    </div>
                    <div class="portfolio-summary__table-entity-secondary">
                      {{ item.cluster_label || "-" }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.risk_priority="{ item }">
                  <v-chip small outlined :color="getRiskTypeColor(item.type)">
                    {{ getRiskTypeLabel(item.type) }}
                  </v-chip>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.issue="{ item }">
                  <div class="portfolio-summary__table-issue">
                    <div class="portfolio-summary__table-issue-primary">
                      {{ getRiskIssueLabel(item.type) }}
                    </div>
                    <div class="portfolio-summary__table-issue-secondary">
                      {{ getRiskIssueHint(item.type) }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.impact_plan="{ item }">
                  <div class="portfolio-summary__table-stack">
                    <div class="portfolio-summary__table-stack-primary">
                      {{ getRiskResolutionLabel(item) }}
                    </div>
                    <div class="portfolio-summary__table-stack-secondary">
                      {{ getRiskImpactLabel(item) }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.action="{ item }">
                  <div class="d-flex align-center justify-center">
                    <rs-row-actions-menu
                      :actions="getRiskMenuActions(item)"
                      @select="handleRowActionSelect"
                    />
                  </div>
                </template>
              </v-data-table>
            </v-tab-item>

            <v-tab-item class="pa-4">
              <div class="portfolio-summary__table-toolbar">
                <div class="portfolio-summary__table-toolbar-copy">
                  <div class="portfolio-summary__table-toolbar-title">
                    Faturalandırma kuyruğu
                  </div>
                  <div class="portfolio-summary__table-toolbar-subtitle">
                    {{ billingTabDescription }}
                  </div>
                </div>

                <div
                  class="portfolio-summary__table-toolbar-filters portfolio-summary__billing-toolbar-actions"
                >
                  <button
                    type="button"
                    class="portfolio-summary__table-filter-chip portfolio-summary__billing-group-chip"
                    :class="{
                      'portfolio-summary__table-filter-chip--active':
                        billingInvoiceGroup === 'corrections',
                    }"
                    :aria-pressed="billingInvoiceGroup === 'corrections'"
                    @click="billingInvoiceGroup = 'corrections'"
                  >
                    Düzeltmeler
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(correctionBillingCandidates.length) }}
                    </span>
                  </button>
                  <button
                    type="button"
                    class="portfolio-summary__table-filter-chip portfolio-summary__billing-group-chip"
                    :class="{
                      'portfolio-summary__table-filter-chip--active':
                        billingInvoiceGroup === 'automatic',
                    }"
                    :aria-pressed="billingInvoiceGroup === 'automatic'"
                    @click="billingInvoiceGroup = 'automatic'"
                  >
                    Zamanlanmış
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(automaticBillingCandidates.length) }}
                    </span>
                  </button>

                  <v-btn
                    v-if="
                      billingInvoiceGroup === 'corrections' &&
                      billingCandidateStatusFilter
                    "
                    small
                    text
                    color="primary"
                    @click="clearBillingCandidateStatusFilter"
                  >
                    Görünümü Temizle
                  </v-btn>
                </div>
              </div>

              <v-data-table
                v-if="billingInvoiceGroup === 'corrections'"
                class="portfolio-summary__data-table"
                :headers="billingCorrectionHeaders"
                :items="filteredCorrectionBillingCandidates"
                :loading="billingCandidatesLoading"
                :items-per-page="billingCandidatesItemsPerPage"
                :footer-props="footerProps"
                dense
                mobile-breakpoint="0"
                :no-data-text="billingCandidatesNoDataText"
                @update:items-per-page="
                  handleBillingCandidatesItemsPerPageUpdated
                "
              >
                <!-- eslint-disable-next-line -->
                <template v-slot:item.customer_scope="{ item }">
                  <div class="portfolio-summary__table-entity">
                    <div class="portfolio-summary__table-entity-primary">
                      {{ item.customer_name || "-" }}
                    </div>
                    <div class="portfolio-summary__table-entity-secondary">
                      {{ formatBillingCandidateClusterLabel(item) }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.billing_type="{ value }">
                  <v-chip small outlined :color="getBillingTypeColor(value)">
                    {{ value || "-" }}
                  </v-chip>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.period_invoice_date="{ item }">
                  <div class="portfolio-summary__table-stack">
                    <div class="portfolio-summary__table-stack-primary">
                      {{
                        formatDateRange(item.coverage_start, item.coverage_end)
                      }}
                    </div>
                    <div class="portfolio-summary__table-stack-secondary">
                      Fatura tarihi: {{ formatDate(item.invoice_date) }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.amount="{ item }">
                  <div class="portfolio-summary__table-amount">
                    {{ formatCurrency(item.estimated_amount) }}
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.action="{ item }">
                  <div class="d-flex align-center justify-center">
                    <rs-row-actions-menu
                      :actions="getBillingCandidateMenuActions(item)"
                      @select="handleRowActionSelect"
                    />
                  </div>
                </template>
              </v-data-table>

              <v-data-table
                v-else
                class="portfolio-summary__data-table"
                :headers="billingAutomaticHeaders"
                :items="automaticBillingCandidates"
                :loading="billingCandidatesLoading"
                :items-per-page="billingCandidatesItemsPerPage"
                :footer-props="footerProps"
                dense
                mobile-breakpoint="0"
                no-data-text="Bir sonraki otomatik kesim için fatura adayı bulunmuyor."
                @update:items-per-page="
                  handleBillingCandidatesItemsPerPageUpdated
                "
              >
                <!-- eslint-disable-next-line -->
                <template v-slot:item.customer_scope="{ item }">
                  <div class="portfolio-summary__table-entity">
                    <div class="portfolio-summary__table-entity-primary">
                      {{ item.customer_name || "-" }}
                    </div>
                    <div class="portfolio-summary__table-entity-secondary">
                      {{ formatBillingCandidateClusterLabel(item) }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.billing_type="{ value }">
                  <v-chip small outlined :color="getBillingTypeColor(value)">
                    {{ value || "-" }}
                  </v-chip>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.period_run_date="{ item }">
                  <div class="portfolio-summary__table-stack">
                    <div class="portfolio-summary__table-stack-primary">
                      {{
                        formatDateRange(item.coverage_start, item.coverage_end)
                      }}
                    </div>
                    <div class="portfolio-summary__table-stack-secondary">
                      Run tarihi: {{ formatDate(item.reference_run_date) }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.amount="{ item }">
                  <div class="portfolio-summary__table-amount">
                    {{ formatCurrency(item.estimated_amount) }}
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.plan="{ item }">
                  <v-chip small outlined color="warning">
                    <v-icon x-small left>mdi-clock-outline</v-icon>
                    Zamanlanmış
                  </v-chip>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.action="{ item }">
                  <div class="d-flex align-center justify-center">
                    <rs-row-actions-menu
                      :actions="getBillingCandidateMenuActions(item)"
                      @select="handleRowActionSelect"
                    />
                  </div>
                </template>
              </v-data-table>
            </v-tab-item>

            <v-tab-item class="pa-4">
              <div class="portfolio-summary__table-toolbar">
                <div class="portfolio-summary__table-toolbar-copy">
                  <div class="portfolio-summary__table-toolbar-title">
                    Aylık faturalandırılan müşteriler
                  </div>
                  <div class="portfolio-summary__table-toolbar-subtitle">
                    Aktif aylık standart ürünü olan müşteriler; ürün dönemi, TYA
                    kapsamı ve kontrol gerektiren kayıtlar tek listede.
                  </div>
                </div>

                <div class="portfolio-summary__table-toolbar-filters">
                  <span
                    class="portfolio-summary__table-filter-chip portfolio-summary__table-filter-chip--active"
                  >
                    Tümü
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(monthlyToolbarStats.total) }}
                    </span>
                  </span>
                  <span class="portfolio-summary__table-filter-chip">
                    Açık uçlu
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(monthlyToolbarStats.openEnded) }}
                    </span>
                  </span>
                  <span class="portfolio-summary__table-filter-chip">
                    Bitişli
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(monthlyToolbarStats.finite) }}
                    </span>
                  </span>
                  <span class="portfolio-summary__table-filter-chip">
                    Kontrol
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(monthlyToolbarStats.attention) }}
                    </span>
                  </span>
                </div>
              </div>

              <v-data-table
                class="portfolio-summary__data-table"
                :headers="monthlyHeaders"
                :items="monthlyBilling"
                :loading="isLoading"
                :server-items-length="meta.monthly_total"
                :items-per-page="monthlyItemsPerPage"
                :page="monthlyPage"
                :footer-props="footerProps"
                dense
                mobile-breakpoint="0"
                :no-data-text="monthlyNoDataText"
                @update:page="handleMonthlyPageUpdated"
                @update:items-per-page="handleMonthlyItemsPerPageUpdated"
              >
                <!-- eslint-disable-next-line -->
                <template v-slot:item.customer_scope="{ item }">
                  <div class="portfolio-summary__table-entity">
                    <div class="portfolio-summary__table-entity-primary">
                      {{ item.customer_name || "-" }}
                    </div>
                    <div class="portfolio-summary__table-entity-secondary">
                      {{ item.cluster_label || "-" }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.billing_type="{ value }">
                  <v-chip small outlined :color="getBillingTypeColor(value)">
                    {{ value || "-" }}
                  </v-chip>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.product_amount="{ item }">
                  <div class="portfolio-summary__table-stack">
                    <div class="portfolio-summary__table-stack-primary">
                      {{ getMonthlyProductName(item) }}
                    </div>
                    <div class="portfolio-summary__table-stack-secondary">
                      {{ getMonthlyProductAmountLabel(item) }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.active_period="{ item }">
                  <div class="portfolio-summary__table-stack">
                    <div class="portfolio-summary__table-stack-primary">
                      {{ item.current_period_label || "-" }}
                    </div>
                    <div class="portfolio-summary__table-stack-secondary">
                      {{ getMonthlyPeriodHint(item) }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.status_note="{ item }">
                  <div class="portfolio-summary__table-stack">
                    <div class="portfolio-summary__table-stack-primary">
                      <v-chip
                        small
                        outlined
                        :color="getMonthlyStatusColor(item)"
                      >
                        {{ getMonthlyStatusLabel(item) }}
                      </v-chip>
                    </div>
                    <div
                      v-if="isMonthlyAttentionNeeded(item)"
                      class="portfolio-summary__table-stack-secondary"
                    >
                      {{ item.note || "Kontrol gerekli" }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.action="{ item }">
                  <div class="d-flex align-center justify-center">
                    <rs-row-actions-menu
                      :actions="getMonthlyMenuActions(item)"
                      @select="handleRowActionSelect"
                    />
                  </div>
                </template>
              </v-data-table>
            </v-tab-item>

            <v-tab-item class="pa-4">
              <div class="portfolio-summary__table-toolbar">
                <div class="portfolio-summary__table-toolbar-copy">
                  <div class="portfolio-summary__table-toolbar-title">
                    Yıllık faturalandırılan müşteriler
                  </div>
                  <div class="portfolio-summary__table-toolbar-subtitle">
                    Yıllık faturalandırma kapsamındaki müşteriler; ürün, tutar,
                    aktif dönem ve kontrol gerektiren kayıtlar tek listede.
                  </div>
                </div>

                <div class="portfolio-summary__table-toolbar-filters">
                  <span
                    class="portfolio-summary__table-filter-chip portfolio-summary__table-filter-chip--active"
                  >
                    Tümü
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(yearlyToolbarStats.total) }}
                    </span>
                  </span>
                  <span class="portfolio-summary__table-filter-chip">
                    Takvim yılı
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(yearlyToolbarStats.calendarYear) }}
                    </span>
                  </span>
                  <span class="portfolio-summary__table-filter-chip">
                    Ara dönemli
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(yearlyToolbarStats.offCycle) }}
                    </span>
                  </span>
                  <span class="portfolio-summary__table-filter-chip">
                    Kontrol
                    <span class="portfolio-summary__table-filter-chip-count">
                      {{ formatInteger(yearlyToolbarStats.attention) }}
                    </span>
                  </span>
                </div>
              </div>

              <v-data-table
                class="portfolio-summary__data-table"
                :headers="yearlyHeaders"
                :items="yearlyBilling"
                :loading="isLoading"
                :server-items-length="meta.yearly_total"
                :items-per-page="yearlyItemsPerPage"
                :page="yearlyPage"
                :footer-props="footerProps"
                dense
                mobile-breakpoint="0"
                :no-data-text="yearlyNoDataText"
                @update:page="handleYearlyPageUpdated"
                @update:items-per-page="handleYearlyItemsPerPageUpdated"
              >
                <!-- eslint-disable-next-line -->
                <template v-slot:item.customer_scope="{ item }">
                  <div class="portfolio-summary__table-entity">
                    <div class="portfolio-summary__table-entity-primary">
                      {{ item.customer_name || "-" }}
                    </div>
                    <div class="portfolio-summary__table-entity-secondary">
                      {{ item.cluster_label || "-" }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.billing_type="{ value }">
                  <v-chip small outlined :color="getBillingTypeColor(value)">
                    {{ value || "-" }}
                  </v-chip>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.product_amount="{ item }">
                  <div class="portfolio-summary__table-stack">
                    <div class="portfolio-summary__table-stack-primary">
                      {{ getYearlyProductName(item) }}
                    </div>
                    <div class="portfolio-summary__table-stack-secondary">
                      {{ getYearlyProductAmountLabel(item) }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.active_period="{ item }">
                  <div class="portfolio-summary__table-stack">
                    <div class="portfolio-summary__table-stack-primary">
                      {{ getYearlyActivePeriodLabel(item) }}
                    </div>
                    <div class="portfolio-summary__table-stack-secondary">
                      {{ getYearlyPeriodHint(item) }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.status_note="{ item }">
                  <div class="portfolio-summary__table-stack">
                    <div class="portfolio-summary__table-stack-primary">
                      <v-chip
                        small
                        outlined
                        :color="getYearlyInventoryColor(item)"
                      >
                        {{ getYearlyInventoryLabel(item) }}
                      </v-chip>
                    </div>
                    <div class="portfolio-summary__table-stack-secondary">
                      {{ getYearlyInventoryNote(item) }}
                    </div>
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.action="{ item }">
                  <div class="d-flex align-center justify-center">
                    <rs-row-actions-menu
                      :actions="getYearlyMenuActions(item)"
                      @select="handleRowActionSelect"
                    />
                  </div>
                </template>
              </v-data-table>
            </v-tab-item>
          </v-tabs-items>
        </v-card>

        <v-dialog v-model="reminderDialog.open" max-width="640">
          <v-card>
            <v-card-title class="pb-1">Hatırlatma Gönder</v-card-title>
            <v-card-subtitle>
              Bildirim yalnızca müşteri ilişkisinin muhatabı olan kişiye
              gönderilir.
            </v-card-subtitle>

            <v-card-text>
              <div v-if="reminderDialog.target" class="mb-4">
                <div class="text-body-2 font-weight-medium">
                  {{ reminderDialog.target.customer_name }}
                </div>
                <div class="text-caption text--secondary mb-2">
                  {{ reminderDialog.target.cluster_label || "TYA bilgisi yok" }}
                </div>
                <div class="text-body-2 mb-1">
                  Yetkili Kişi: <strong>{{ reminderRecipientName }}</strong>
                </div>
                <div class="text-caption text--secondary">
                  <span v-if="reminderDialog.target.authorized_user_phone">
                    SMS: {{ reminderDialog.target.authorized_user_phone }}
                  </span>
                  <span
                    v-if="
                      reminderDialog.target.authorized_user_phone &&
                      reminderDialog.target.authorized_user_email
                    "
                  >
                    •
                  </span>
                  <span v-if="reminderDialog.target.authorized_user_email">
                    Mail: {{ reminderDialog.target.authorized_user_email }}
                  </span>
                </div>
              </div>

              <v-form ref="reminderForm" @submit.prevent="sendReminder">
                <v-row dense>
                  <v-col cols="12" sm="4">
                    <rs-select
                      label="Kanal"
                      v-model="reminderDialog.form.notification_channel_id"
                      :items="reminderChannelOptions"
                      :rules="[rules.required]"
                      required
                    />
                  </v-col>

                  <v-col cols="12" sm="8">
                    <rs-text-field
                      label="Başlık (Opsiyonel)"
                      v-model="reminderDialog.form.subject"
                      :rules="[
                        rules.maxLength(reminderDialog.form.subject, 120),
                      ]"
                    />
                  </v-col>

                  <v-col cols="12">
                    <rs-textarea
                      label="Mesaj"
                      v-model="reminderDialog.form.body"
                      :rules="[
                        rules.required,
                        rules.maxLength(reminderDialog.form.body, 1000),
                      ]"
                      rows="4"
                      required
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>

            <v-card-actions class="justify-end">
              <v-btn
                text
                :disabled="reminderDialog.sending"
                @click="closeReminderDialog"
              >
                Vazgeç
              </v-btn>
              <v-btn
                color="primary"
                :loading="reminderDialog.sending"
                @click="sendReminder"
              >
                Gönder
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="yearlyPreviewDialog.open" max-width="980">
          <v-card>
            <v-card-title class="pb-1">
              Yıllık Faturalandırma Önizlemesi
            </v-card-title>
            <v-card-subtitle>
              Taslak fatura server tarafında mevcut relation ve fiyat kaynağına
              göre doğrulanarak üretilir.
            </v-card-subtitle>

            <v-card-text>
              <div
                v-if="yearlyPreviewDialog.loading"
                class="py-10 d-flex justify-center"
              >
                <v-progress-circular indeterminate color="primary" />
              </div>

              <template v-else-if="yearlyPreviewDialog.preview">
                <v-row dense>
                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text--secondary">Müşteri</div>
                    <div class="font-weight-medium">
                      {{ yearlyPreviewDialog.preview.customer_name }}
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text--secondary">TYA Kapsamı</div>
                    <div class="font-weight-medium">
                      {{ yearlyPreviewDialog.preview.cluster_label || "-" }}
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text--secondary">Hedef Yıl</div>
                    <div class="font-weight-medium">
                      {{ yearlyPreviewDialog.preview.target_year }}
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text--secondary">İşlem Tipi</div>
                    <div class="font-weight-medium">
                      {{
                        getYearlyActionTypeLabel(
                          yearlyPreviewDialog.preview.action_type
                        )
                      }}
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text--secondary">
                      Mevcut Kapsam
                    </div>
                    <div class="font-weight-medium">
                      {{
                        yearlyPreviewDialog.preview.current_coverage_label ||
                        "-"
                      }}
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6" md="4">
                    <div class="text-caption text--secondary">Yeni Dönem</div>
                    <div class="font-weight-medium">
                      {{
                        yearlyPreviewDialog.preview.suggested_action_label ||
                        "-"
                      }}
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6">
                    <div class="text-caption text--secondary">
                      Fiyat Kaynağı
                    </div>
                    <div class="font-weight-medium">
                      {{
                        yearlyPreviewDialog.preview.pricing_source
                          ?.product_name || "-"
                      }}
                      <span
                        v-if="
                          yearlyPreviewDialog.preview.pricing_source
                            ?.amount_per_unit !== undefined
                        "
                      >
                        •
                        {{
                          formatCurrency(
                            yearlyPreviewDialog.preview.pricing_source
                              .amount_per_unit
                          )
                        }}
                      </span>
                    </div>
                    <div class="text-caption text--secondary">
                      {{
                        formatDateRange(
                          yearlyPreviewDialog.preview.pricing_source?.starts_on,
                          yearlyPreviewDialog.preview.pricing_source?.ends_on
                        )
                      }}
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6">
                    <div class="text-caption text--secondary">Sebep / Not</div>
                    <div class="font-weight-medium">
                      {{ yearlyPreviewDialog.preview.reason || "-" }}
                    </div>
                  </v-col>

                  <v-col
                    cols="12"
                    v-if="
                      yearlyPreviewDialog.preview.action_type === 'bridge' &&
                      yearlyPreviewDialog.preview.next_anchor_year
                    "
                  >
                    <v-alert dense outlined type="info" class="mb-0">
                      Bu kayıt takvim yılına hizalı değil. Sistem bir kerelik
                      köprü dönem üretecek; sonraki tam yenileme yılı
                      {{ yearlyPreviewDialog.preview.next_anchor_year }}
                      olacaktır.
                    </v-alert>
                  </v-col>
                </v-row>

                <v-row dense class="mt-2 mb-2">
                  <v-col cols="12" md="4">
                    <v-sheet outlined rounded class="pa-3">
                      <div class="text-caption text--secondary mb-1">
                        Fatura Tarihi
                      </div>
                      <div class="text-subtitle-1 font-weight-medium">
                        {{
                          formatDate(
                            yearlyPreviewDialog.preview.invoice.invoiced_on
                          )
                        }}
                      </div>
                    </v-sheet>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-sheet outlined rounded class="pa-3">
                      <div class="text-caption text--secondary mb-1">
                        Satır Sayısı
                      </div>
                      <div class="text-subtitle-1 font-weight-medium">
                        {{
                          formatInteger(
                            yearlyPreviewDialog.preview.invoice.item_count
                          )
                        }}
                      </div>
                    </v-sheet>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-sheet outlined rounded class="pa-3">
                      <div class="text-caption text--secondary mb-1">
                        KDV Dahil Toplam
                      </div>
                      <div class="text-subtitle-1 font-weight-medium">
                        {{
                          formatCurrency(
                            yearlyPreviewDialog.preview.invoice
                              .amount_tax_included
                          )
                        }}
                      </div>
                    </v-sheet>
                  </v-col>
                </v-row>

                <v-simple-table dense class="portfolio-summary__preview-table">
                  <template #default>
                    <thead>
                      <tr>
                        <th>TYA</th>
                        <th>Dönem</th>
                        <th class="text-right">BB</th>
                        <th class="text-right">Birim Fiyat</th>
                        <th class="text-right">Gün</th>
                        <th class="text-right">Tutar</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(line, index) in yearlyPreviewDialog.preview
                          .items"
                        :key="`yearly-preview-line-${index}`"
                      >
                        <td>{{ line.cluster_name }}</td>
                        <td>
                          {{
                            formatDateRange(
                              line.period_starts_on,
                              line.period_ends_on
                            )
                          }}
                        </td>
                        <td class="text-right">
                          {{ formatInteger(line.units) }}
                        </td>
                        <td class="text-right">
                          {{ formatCurrency(line.amount_per_unit) }}
                        </td>
                        <td class="text-right">
                          {{ formatInteger(line.invoiced_days) }}
                        </td>
                        <td class="text-right">
                          {{ formatCurrency(line.amount_tax_included) }}
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </template>
            </v-card-text>

            <v-card-actions class="justify-end">
              <v-btn
                text
                :disabled="yearlyPreviewDialog.applying"
                @click="closeYearlyPreviewDialog"
              >
                Kapat
              </v-btn>
              <v-btn
                v-if="yearlyPreviewDialog.item"
                text
                :disabled="yearlyPreviewDialog.applying"
                :to="getCustomerRoute(yearlyPreviewDialog.item.customer_id)"
              >
                Müşteriye Git
              </v-btn>
              <v-btn
                v-if="yearlyPreviewDialog.preview"
                color="primary"
                :loading="yearlyPreviewDialog.applying"
                @click="applyYearlyPreviewAction"
              >
                {{
                  getYearlyApplyButtonLabel(
                    yearlyPreviewDialog.preview.action_type
                  )
                }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="billingCandidatePreviewDialog.open" max-width="980">
          <v-card>
            <v-card-title class="pb-1">
              Otomatik Kesilecek Fatura Önizlemesi
            </v-card-title>
            <v-card-subtitle>
              Taslak mevcut otomatik faturalandırma mantığına göre doğrulanarak
              hazırlanır.
            </v-card-subtitle>

            <v-card-text v-if="billingCandidatePreviewDialog.item">
              <v-row dense>
                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text--secondary">Müşteri</div>
                  <div class="font-weight-medium">
                    {{ billingCandidatePreviewDialog.item.customer_name }}
                  </div>
                </v-col>

                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text--secondary">TYA Kapsamı</div>
                  <div class="font-weight-medium">
                    {{
                      formatBillingCandidateClusterLabel(
                        billingCandidatePreviewDialog.item
                      )
                    }}
                  </div>
                </v-col>

                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text--secondary">Faturalandırma</div>
                  <div class="font-weight-medium">
                    {{ billingCandidatePreviewDialog.item.billing_type }}
                  </div>
                </v-col>

                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text--secondary">Fatura Tarihi</div>
                  <div class="font-weight-medium">
                    {{
                      formatDate(
                        billingCandidatePreviewDialog.item.invoice_date
                      )
                    }}
                  </div>
                </v-col>

                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text--secondary">
                    Doğal Kesim Tarihi
                  </div>
                  <div class="font-weight-medium">
                    {{
                      formatDate(
                        billingCandidatePreviewDialog.item.natural_run_date ||
                          billingCandidatePreviewDialog.item.reference_run_date
                      )
                    }}
                  </div>
                </v-col>

                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text--secondary">Kapsam</div>
                  <div class="font-weight-medium">
                    {{
                      formatDateRange(
                        billingCandidatePreviewDialog.item.coverage_start,
                        billingCandidatePreviewDialog.item.coverage_end
                      )
                    }}
                  </div>
                </v-col>

                <v-col cols="12" sm="6" md="4">
                  <div class="text-caption text--secondary">Durum</div>
                  <div class="font-weight-medium">
                    {{
                      getBillingCandidateStatusLabel(
                        billingCandidatePreviewDialog.item.status
                      )
                    }}
                  </div>
                </v-col>
              </v-row>

              <v-row dense class="mt-2 mb-2">
                <v-col cols="12" md="6">
                  <v-sheet outlined rounded class="pa-3">
                    <div class="text-caption text--secondary mb-1">
                      Tahmini Toplam
                    </div>
                    <div class="text-subtitle-1 font-weight-medium">
                      {{
                        formatCurrency(
                          billingCandidatePreviewDialog.item.estimated_amount
                        )
                      }}
                    </div>
                  </v-sheet>
                </v-col>

                <v-col cols="12" md="6">
                  <v-sheet outlined rounded class="pa-3">
                    <div class="text-caption text--secondary mb-1">
                      Satır Sayısı
                    </div>
                    <div class="text-subtitle-1 font-weight-medium">
                      {{
                        formatInteger(
                          billingCandidatePreviewDialog.item.items_preview
                            ?.length || 0
                        )
                      }}
                    </div>
                  </v-sheet>
                </v-col>
              </v-row>

              <v-alert
                v-if="billingCandidatePreviewDialog.item.status === 'ready'"
                dense
                outlined
                type="info"
                class="mb-4"
              >
                Bu düzeltme faturası oluşturulduğunda resmi fatura tarihi
                {{
                  formatDate(billingCandidatePreviewDialog.item.invoice_date)
                }}
                olur. Kapsam ise geçmiş dönemi kapatır.
              </v-alert>

              <v-alert
                v-if="billingCandidatePreviewDialog.item.status === 'scheduled'"
                dense
                outlined
                type="info"
                class="mb-4"
              >
                Bu kayıt doğal auto-billing semantiğine göre
                {{
                  formatDate(
                    billingCandidatePreviewDialog.item.reference_run_date
                  )
                }}
                tarihinde oluşturulabilir.
              </v-alert>

              <v-simple-table dense class="portfolio-summary__preview-table">
                <template #default>
                  <thead>
                    <tr>
                      <th>TYA</th>
                      <th>Dönem</th>
                      <th class="text-right">BB</th>
                      <th class="text-right">Birim Fiyat</th>
                      <th class="text-right">Gün</th>
                      <th class="text-right">Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(line, index) in billingCandidatePreviewDialog.item
                        .items_preview"
                      :key="`billing-candidate-line-${index}`"
                    >
                      <td>{{ line.cluster_label }}</td>
                      <td>
                        {{
                          formatDateRange(
                            line.period_starts_on,
                            line.period_ends_on
                          )
                        }}
                      </td>
                      <td class="text-right">
                        {{ formatInteger(line.units) }}
                      </td>
                      <td class="text-right">
                        {{ formatCurrency(line.amount_per_unit) }}
                      </td>
                      <td class="text-right">
                        {{ formatInteger(line.invoiced_days) }}
                      </td>
                      <td class="text-right">
                        {{ formatCurrency(line.total_amount) }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>

            <v-card-actions class="justify-end">
              <v-btn
                text
                :disabled="
                  billingCandidatePreviewDialog.item &&
                  isBillingCandidateCreating(billingCandidatePreviewDialog.item)
                "
                @click="closeBillingCandidatePreviewDialog"
              >
                Kapat
              </v-btn>
              <v-btn
                v-if="billingCandidatePreviewDialog.item"
                text
                target="_blank"
                rel="noopener noreferrer"
                :href="
                  resolveRouteHref(
                    getCustomerRoute(
                      billingCandidatePreviewDialog.item.customer_id
                    )
                  )
                "
              >
                Müşteriye Git
              </v-btn>
              <v-btn
                v-if="billingCandidatePreviewDialog.item"
                color="primary"
                :loading="
                  isBillingCandidateCreating(billingCandidatePreviewDialog.item)
                "
                :disabled="
                  billingCandidatePreviewDialog.item.status !== 'ready' ||
                  (billingCandidateCreateApplying &&
                    !isBillingCandidateCreating(
                      billingCandidatePreviewDialog.item
                    ))
                "
                @click="
                  createBillingCandidateInvoice(
                    billingCandidatePreviewDialog.item
                  )
                "
              >
                Oluştur
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <portfolio-year-end-transition-dialog
          v-model="yearEndTransitionDialogOpen"
          :default-snapshot-date="getYearEndTransitionSnapshotDate()"
          @applied="load"
        />

        <billing-operation-wizard
          v-model="billingOperationWizard.open"
          :source="billingOperationWizard.source"
          :operation-type="billingOperationWizard.operationType"
          @applied="handleBillingOperationApplied"
        />
      </v-card-text>
    </div>
  </div>
</template>

<script>
import validation from "@/core/validation";
import { hasPermissions, isPage } from "@/view/mixins";
import { numberToLocaleFormat } from "@/core/helpers";
import { mapGetters } from "vuex";
import PortfolioYearEndTransitionDialog from "./components/Wizard/PortfolioYearEndTransitionDialog.vue";
import BillingOperationWizard from "./components/Operations/BillingOperationWizard.vue";
import {
  getOperationTypeForIssue,
  isWizardOperationSupported,
} from "./components/Operations/operationRegistry";

export default {
  name: "AdminCustomerPortfolioSummary",
  components: {
    BillingOperationWizard,
    PortfolioYearEndTransitionDialog,
  },
  mixins: [hasPermissions, isPage],
  data() {
    return {
      rules: validation,
      isLoading: false,
      referenceDate: new Date().toISOString().slice(0, 10),
      lastLoadedAt: null,
      pageMeta: {
        title: "Portföy Özeti",
      },
      selectedTab: 0,
      footerProps: {
        itemsPerPageOptions: [-1, 10, 20, 50],
      },
      kpis: {
        active_customer_count: 0,
        monthly_customer_count: 0,
        yearly_customer_count: 0,
        overdue_customer_count: 0,
        expiring_product_count: 0,
        expiring_relation_count: 0,
        missing_setup_count: 0,
        month_invoiced_total: 0,
        month_collected_total: 0,
      },
      priorityActions: [],
      risks: [],
      billingCandidates: [],
      automaticBillingCandidateList: [],
      creatingBillingCandidateMap: {},
      billingCandidateCreateApplying: false,
      billingInvoiceGroup: "corrections",
      billingCandidateStatusFilter: null,
      monthlyBilling: [],
      yearlyBilling: [],
      priorityPage: 1,
      priorityItemsPerPage: -1,
      priorityFilterType: "overdue",
      riskPage: 1,
      riskItemsPerPage: -1,
      riskFilterType: null,
      billingCandidatesItemsPerPage: -1,
      monthlyPage: 1,
      monthlyItemsPerPage: -1,
      yearlyPage: 1,
      yearlyItemsPerPage: -1,
      yearlyTargetYear: new Date().getFullYear() + 1,
      billingCandidatesLoading: false,
      yearlySummary: {
        ready_count: 0,
        bridge_required_count: 0,
        not_ready_count: 0,
      },
      reminderDialog: {
        open: false,
        sending: false,
        target: null,
        form: {
          notification_channel_id: null,
          subject: "",
          body: "",
        },
      },
      yearlyPreviewDialog: {
        open: false,
        loading: false,
        applying: false,
        item: null,
        preview: null,
      },
      billingCandidatePreviewDialog: {
        open: false,
        applying: false,
        item: null,
      },
      billingOperationWizard: {
        open: false,
        source: null,
        operationType: null,
      },
      yearEndTransitionDialogOpen: false,
      meta: {
        priority_total: 0,
        risk_total: 0,
        monthly_total: 0,
        yearly_total: 0,
        priority_page: 1,
        priority_limit: -1,
        priority_last_page: 1,
        risk_page: 1,
        risk_limit: -1,
        risk_last_page: 1,
        monthly_page: 1,
        monthly_limit: -1,
        monthly_last_page: 1,
        yearly_page: 1,
        yearly_limit: -1,
        yearly_last_page: 1,
        target_year: new Date().getFullYear() + 1,
        priority_type: null,
        risk_type: null,
      },
      priorityHeaders: [
        {
          text: "Müşteri / TYA",
          value: "customer_scope",
          sortable: false,
          width: "30%",
        },
        {
          text: "Faturalandırma",
          value: "billing_type",
          sortable: false,
          width: "120px",
        },
        {
          text: "İlk Geciken Fatura",
          value: "critical_date",
          sortable: false,
          width: "150px",
        },
        {
          text: "Gecikme Özeti",
          value: "overdue_summary",
          sortable: false,
          width: "170px",
        },
        {
          text: "Tahsilat Riski",
          value: "collection_risk",
          sortable: false,
          width: "150px",
        },
        {
          text: "Aksiyon",
          value: "action",
          sortable: false,
          align: "center",
          width: "72px",
        },
      ],
      riskHeaders: [
        {
          text: "Müşteri / TYA",
          value: "customer_scope",
          sortable: false,
          width: "28%",
        },
        {
          text: "Öncelik",
          value: "risk_priority",
          sortable: false,
          width: "110px",
        },
        {
          text: "Sorun",
          value: "issue",
          sortable: false,
          width: "220px",
        },
        {
          text: "Etki / Önerilen Çözüm",
          value: "impact_plan",
          sortable: false,
        },
        {
          text: "Aksiyon",
          value: "action",
          sortable: false,
          align: "center",
          width: "72px",
        },
      ],
      billingCorrectionHeaders: [
        {
          text: "Müşteri / TYA",
          value: "customer_scope",
          sortable: false,
          width: "32%",
        },
        {
          text: "Faturalandırma",
          value: "billing_type",
          sortable: false,
          width: "120px",
        },
        {
          text: "Dönem / Fatura Tarihi",
          value: "period_invoice_date",
          sortable: false,
        },
        {
          text: "Tutar",
          value: "amount",
          sortable: false,
          align: "end",
          width: "130px",
        },
        {
          text: "Aksiyon",
          value: "action",
          sortable: false,
          align: "center",
          width: "72px",
        },
      ],
      billingAutomaticHeaders: [
        {
          text: "Müşteri / TYA",
          value: "customer_scope",
          sortable: false,
          width: "32%",
        },
        {
          text: "Faturalandırma",
          value: "billing_type",
          sortable: false,
          width: "120px",
        },
        {
          text: "Dönem / Run Tarihi",
          value: "period_run_date",
          sortable: false,
        },
        {
          text: "Tutar",
          value: "amount",
          sortable: false,
          align: "end",
          width: "130px",
        },
        {
          text: "Plan",
          value: "plan",
          sortable: false,
          width: "140px",
        },
        {
          text: "Aksiyon",
          value: "action",
          sortable: false,
          align: "center",
          width: "72px",
        },
      ],
      monthlyHeaders: [
        {
          text: "Müşteri / TYA",
          value: "customer_scope",
          sortable: false,
          width: "27%",
        },
        {
          text: "Faturalandırma",
          value: "billing_type",
          sortable: false,
          width: "120px",
        },
        {
          text: "Ürün / Tutar",
          value: "product_amount",
          sortable: false,
          width: "23%",
        },
        {
          text: "Aktif Dönem",
          value: "active_period",
          sortable: false,
          width: "20%",
        },
        {
          text: "Durum / Not",
          value: "status_note",
          sortable: false,
        },
        {
          text: "Aksiyon",
          value: "action",
          sortable: false,
          align: "center",
          width: "72px",
        },
      ],
      yearlyHeaders: [
        {
          text: "Müşteri / TYA",
          value: "customer_scope",
          sortable: false,
          width: "27%",
        },
        {
          text: "Faturalandırma",
          value: "billing_type",
          sortable: false,
          width: "120px",
        },
        {
          text: "Ürün / Tutar",
          value: "product_amount",
          sortable: false,
          width: "23%",
        },
        {
          text: "Aktif Dönem",
          value: "active_period",
          sortable: false,
          width: "20%",
        },
        {
          text: "Durum / Not",
          value: "status_note",
          sortable: false,
        },
        {
          text: "Aksiyon",
          value: "action",
          sortable: false,
          align: "center",
          width: "72px",
        },
      ],
    };
  },
  computed: {
    ...mapGetters(["notificationChannelListIds"]),
    billingTabBadgeCount() {
      return (
        this.correctionBillingCandidates.length +
        this.automaticBillingCandidates.length
      );
    },
    billingTabDescription() {
      if (this.billingInvoiceGroup === "automatic") {
        return "Bir sonraki run tarihinde otomatik kesilecek fatura adayları.";
      }

      return "Geçmiş döneme ait bugün oluşturulabilecek düzeltme faturaları.";
    },
    currentBillingPeriodLabel() {
      return this.formatMonthYear(this.referenceDate);
    },
    lastLoadedAtLabel() {
      if (!this.lastLoadedAt) {
        return "-";
      }

      return new Intl.DateTimeFormat("tr-TR", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(this.lastLoadedAt);
    },
    nextAutomaticBillingRunLabel() {
      if (!this.nextAutomaticBillingRunDate) {
        return "-";
      }

      return this.formatDateTime(
        this.parseDateOnly(this.nextAutomaticBillingRunDate)
      );
    },
    nextAutomaticBillingRunDate() {
      const referenceDate = this.parseDateOnly(this.referenceDate);

      if (!referenceDate) {
        return null;
      }

      return this.formatDateForApi(
        new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 1)
      );
    },
    readyBillingCandidateCount() {
      return this.billingCandidates.filter((item) => item.status === "ready")
        .length;
    },
    scheduledBillingCandidateCount() {
      return this.automaticBillingCandidates.length;
    },
    correctionBillingCandidates() {
      return this.billingCandidates.filter((item) => item.status === "ready");
    },
    automaticBillingCandidates() {
      return Array.isArray(this.automaticBillingCandidateList)
        ? this.automaticBillingCandidateList
        : [];
    },
    overdueToolbarStats() {
      const items = Array.isArray(this.priorityActions)
        ? this.priorityActions
        : [];
      const metaTotal = Number(this.meta.priority_total);

      return {
        total:
          Number.isFinite(metaTotal) && metaTotal > 0
            ? metaTotal
            : items.length,
        over30: items.filter((item) => {
          const overdueDays = this.getOverdueDayCount(item);

          return overdueDays !== null && overdueDays >= 30;
        }).length,
        yearly: items.filter((item) => this.isYearlyBillingType(item)).length,
      };
    },
    riskToolbarStats() {
      const items = Array.isArray(this.risks) ? this.risks : [];
      const metaTotal = Number(this.meta.risk_total);

      return {
        total:
          Number.isFinite(metaTotal) && metaTotal > 0
            ? metaTotal
            : items.length,
        critical: items.filter(
          (item) => this.getRiskTypeLabel(item.type) === "Kritik"
        ).length,
        missingProduct: items.filter((item) => item.type === "missing_product")
          .length,
        missingAuthorizedUser: items.filter(
          (item) => item.type === "missing_authorized_user"
        ).length,
      };
    },
    monthlyToolbarStats() {
      const items = Array.isArray(this.monthlyBilling)
        ? this.monthlyBilling
        : [];
      const metaTotal = Number(this.meta.monthly_total);

      return {
        total:
          Number.isFinite(metaTotal) && metaTotal > 0
            ? metaTotal
            : items.length,
        openEnded: items.filter((item) => this.isMonthlyOpenEnded(item)).length,
        finite: items.filter((item) => this.isMonthlyFinitePeriod(item)).length,
        attention: items.filter((item) => this.isMonthlyAttentionNeeded(item))
          .length,
      };
    },
    yearlyToolbarStats() {
      const items = Array.isArray(this.yearlyBilling) ? this.yearlyBilling : [];
      const metaTotal = Number(this.meta.yearly_total);

      return {
        total:
          Number.isFinite(metaTotal) && metaTotal > 0
            ? metaTotal
            : items.length,
        calendarYear: items.filter((item) => this.isYearlyCalendarYear(item))
          .length,
        offCycle: items.filter((item) => this.isYearlyOffCycle(item)).length,
        attention: items.filter((item) => this.isYearlyAttentionNeeded(item))
          .length,
      };
    },
    filteredCorrectionBillingCandidates() {
      if (!this.billingCandidateStatusFilter) {
        return this.correctionBillingCandidates;
      }

      return this.correctionBillingCandidates.filter(
        (item) => item.status === this.billingCandidateStatusFilter
      );
    },
    operationSummaryItems() {
      return [
        {
          key: "risks",
          title: "Riskli kayıtlar",
          caption:
            "Faturalama başlamadan önce çözülmesi gereken veri eksikleri.",
          actionLabel: "Riskleri incele",
          count: this.meta.risk_total || this.kpis.missing_setup_count,
          icon: "mdi-alert-circle-outline",
          tone: "danger",
          tab: 1,
        },
        {
          key: "ready-corrections",
          title: "Şimdi Oluşturulabilir",
          caption:
            "Düzeltme sonrası bugün oluşturulabilecek düzeltme faturaları.",
          actionLabel: "Hazır faturaları aç",
          count: this.readyBillingCandidateCount,
          icon: "mdi-file-document-plus-outline",
          tone: "ready",
          tab: 2,
          billingInvoiceGroup: "corrections",
          billingCandidateStatus: "ready",
        },
        {
          key: "scheduled-corrections",
          title: "Otomatik Kesilecek",
          caption: "Uygun run tarihinde otomatik kesilecek faturalar.",
          actionLabel: "Zamanlananları gör",
          count: this.scheduledBillingCandidateCount,
          icon: "mdi-timer-outline",
          tone: "scheduled",
          tab: 2,
          billingInvoiceGroup: "automatic",
          billingCandidateStatus: "scheduled",
        },
        {
          key: "overdue",
          title: "Geciken ödemeler",
          caption: "15 günü geçen açık fatura taşıyan müşteriler.",
          actionLabel: "Takip listesine git",
          count: this.kpis.overdue_customer_count,
          icon: "mdi-timer-sand",
          tone: "overdue",
          tab: 0,
          priorityType: "overdue",
        },
      ];
    },
    kpiCards() {
      return [
        {
          key: "active_customer_count",
          label: "Aktif Portföy",
          variant: "portfolio",
          value: this.kpis.active_customer_count,
          caption: "Bugün aktif ürün kaydı bulunan müşteri.",
          tooltip: "Bugün en az bir aktif ürün kaydı bulunan müşteri sayısı.",
          iconText: "●",
          breakdown: [
            {
              label: "Aylık",
              value: this.kpis.monthly_customer_count,
            },
            {
              label: "Yıllık",
              value: this.kpis.yearly_customer_count,
            },
          ],
        },
        {
          key: "monthly_customer_count",
          label: "Aylık Müşteriler",
          value: this.kpis.monthly_customer_count,
          caption: "Aktif aylık standart ürün ile faturalanan kayıtlar.",
          tooltip: "Aktif aylık standart ürün ile faturalanan müşteri sayısı.",
          iconText: "A",
          actionable: true,
          drilldown: {
            tab: 3,
          },
        },
        {
          key: "yearly_customer_count",
          label: "Yıllık Müşteriler",
          value: this.kpis.yearly_customer_count,
          caption: "Aktif yıllık standart ürün ile faturalanan kayıtlar.",
          tooltip: "Aktif yıllık standart ürün ile faturalanan müşteri sayısı.",
          iconText: "Y",
          actionable: true,
          drilldown: {
            tab: 4,
          },
        },
        {
          key: "monthly_invoice_flow",
          label: "Bu Ayki Fatura Akışı",
          variant: "invoice-flow",
          tooltip:
            "Bu ay kesilen faturalar ile bu ay başarılı tahsil edilen ödeme toplamı.",
          invoicedValue: this.kpis.month_invoiced_total,
          collectedValue: this.kpis.month_collected_total,
          iconText: "₺",
        },
      ];
    },
    reminderChannelOptions() {
      return this.notificationChannelListIds.filter((item) => {
        if (item.id === 2) {
          return !!this.reminderDialog.target?.authorized_user_phone;
        }

        if (item.id === 4) {
          return !!this.reminderDialog.target?.authorized_user_email;
        }

        return false;
      });
    },
    reminderRecipientName() {
      const target = this.reminderDialog.target;

      if (!target) {
        return "-";
      }

      return (
        target.authorized_user_name ||
        (target.authorized_user_id
          ? `#${target.authorized_user_id}`
          : "Muhatap kişi bulunamadı")
      );
    },
    priorityNoDataText() {
      if (this.priorityFilterType) {
        return "Seçili görünüme uyan kayıt bulunmuyor.";
      }

      return "Bugün öncelikli takip gerektiren kayıt bulunmuyor.";
    },
    riskNoDataText() {
      if (this.riskFilterType) {
        return "Seçili görünüme uyan kayıt bulunmuyor.";
      }

      return "Aktif setup veya ilişki çakışması riski bulunmuyor.";
    },
    monthlyNoDataText() {
      return "Aktif aylık müşteri portföyü bulunmuyor.";
    },
    billingCandidatesNoDataText() {
      if (this.billingCandidateStatusFilter === "ready") {
        return "Geçmiş döneme dair oluşturulabilir düzeltme faturası bulunmuyor.";
      }

      if (this.billingCandidateStatusFilter === "scheduled") {
        return "Bir sonraki otomatik kesim için fatura adayı bulunmuyor.";
      }

      return "Geçmiş döneme dair düzeltme faturası bulunmuyor.";
    },
    targetYearOptions() {
      const baseYear = Number(String(this.referenceDate || "").slice(0, 4));
      const startYear = Number.isFinite(baseYear)
        ? baseYear
        : new Date().getFullYear();

      return [startYear, startYear + 1, startYear + 2, startYear + 3];
    },
    yearlyNoDataText() {
      return "Seçili hedef yıl için yıllık müşteri portföyü bulunmuyor.";
    },
  },
  watch: {
    billingInvoiceGroup(value) {
      if (value !== "corrections") {
        this.billingCandidateStatusFilter = null;
      }
    },
    selectedTab(value) {
      if (value !== 0 || this.priorityFilterType === "overdue") {
        return;
      }

      this.priorityFilterType = "overdue";
      this.priorityPage = 1;
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;
      this.loadBillingCandidates();

      const params = {
        priority_page: this.priorityPage,
        priority_limit: this.priorityItemsPerPage,
        risk_page: this.riskPage,
        risk_limit: this.riskItemsPerPage,
        monthly_page: this.monthlyPage,
        monthly_limit: this.monthlyItemsPerPage,
        yearly_page: this.yearlyPage,
        yearly_limit: this.yearlyItemsPerPage,
        target_year: this.yearlyTargetYear,
      };

      if (this.priorityFilterType) {
        params.priority_type = this.priorityFilterType;
      }

      if (this.riskFilterType) {
        params.risk_type = this.riskFilterType;
      }

      this.$api
        .query("api-super-admin/customers/portfolio-summary", {
          params,
        })
        .then((response) => {
          const payload = response?.data?.data || {};

          this.referenceDate = payload.reference_date || this.referenceDate;
          this.kpis = payload.kpis || this.kpis;
          this.priorityActions = payload.priority_actions || [];
          this.risks = payload.risks || [];
          this.monthlyBilling = payload.monthly_billing || [];
          this.yearlyBilling = payload.yearly_billing || [];
          this.yearlySummary = payload.yearly_summary || this.yearlySummary;
          this.meta = payload.meta || this.meta;
          this.lastLoadedAt = new Date();
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    loadBillingCandidates() {
      this.billingCandidatesLoading = true;

      const correctionRequest = this.$api.query(
        "api-super-admin/customer-invoices/billing-candidates",
        {
          params: {
            scope: "correction_missing",
          },
        }
      );
      const automaticRequest = this.$api.query(
        "api-super-admin/customer-invoices/billing-candidates",
        {
          params: {
            scope: "automatic_scheduled",
          },
        }
      );

      Promise.all([correctionRequest, automaticRequest])
        .then(([correctionResponse, automaticResponse]) => {
          this.billingCandidates = correctionResponse?.data?.data || [];
          this.automaticBillingCandidateList =
            automaticResponse?.data?.data || [];
        })
        .catch((error) => {
          this.handleBillingCandidateError(error);
        })
        .finally(() => {
          this.billingCandidatesLoading = false;
        });
    },
    handleError(error) {
      if (!error || !error.response || !error.response.status) {
        throw error;
      }

      if (error.response.status === 401) {
        this.$toast.error("Kullanıcı girişi yapmalısınız.");
      } else if (error.response.status === 403) {
        this.$toast.error("Bunu yapmaya yetkili değilsiniz.");
      } else if (error.response.status === 500) {
        this.$toast.error("Sunucu hatası. Portföy özeti yüklenemedi.");
      } else {
        this.$toast.error("Portföy özeti yüklenemedi.");
      }
    },
    formatInteger(value) {
      return new Intl.NumberFormat("tr-TR", {
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
    },
    formatCurrency(value) {
      return `${numberToLocaleFormat(Number(value || 0))} ₺`;
    },
    formatKpiCurrency(value) {
      return `${new Intl.NumberFormat("tr-TR", {
        maximumFractionDigits: 0,
      }).format(Number(value || 0))} ₺`;
    },
    getInvoiceFlowCollectionRate(card) {
      const invoicedValue = Number(card?.invoicedValue || 0);
      const collectedValue = Number(card?.collectedValue || 0);

      if (invoicedValue <= 0) {
        return 0;
      }

      return Math.min(100, Math.round((collectedValue / invoicedValue) * 100));
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      const [year, month, day] = String(value).slice(0, 10).split("-");

      if (!year || !month || !day) {
        return value;
      }

      return `${day}.${month}.${year}`;
    },
    formatDateForApi(value) {
      if (!value) {
        return null;
      }

      const date = value instanceof Date ? value : new Date(value);

      if (Number.isNaN(date.getTime())) {
        return null;
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },
    formatDateTime(value) {
      if (!value) {
        return "-";
      }

      const date = value instanceof Date ? value : new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "-";
      }

      return `${this.formatDate(this.formatDateForApi(date))} ${String(
        date.getHours()
      ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
    },
    parseDateOnly(value) {
      if (!value) {
        return null;
      }

      const [year, month, day] = String(value).slice(0, 10).split("-");

      if (!year || !month || !day) {
        return null;
      }

      const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));

      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    },
    formatMonthYear(value) {
      if (!value) {
        return "-";
      }

      const [year, month] = String(value).split("-");
      const monthIndex = Number(month) - 1;
      const monthNames = [
        "Ocak",
        "Şubat",
        "Mart",
        "Nisan",
        "Mayıs",
        "Haziran",
        "Temmuz",
        "Ağustos",
        "Eylül",
        "Ekim",
        "Kasım",
        "Aralık",
      ];

      if (!year || monthIndex < 0 || monthIndex > 11) {
        return "-";
      }

      return `${monthNames[monthIndex]} ${year}`;
    },
    formatDateRange(startsOn, endsOn) {
      if (!startsOn || !endsOn) {
        return "-";
      }

      return `${this.formatDate(startsOn)} - ${this.formatDate(endsOn)}`;
    },
    formatOpenDateRange(startsOn, endsOn) {
      if (!startsOn && !endsOn) {
        return "-";
      }

      return `${startsOn ? this.formatDate(startsOn) : "-"} - ${
        endsOn ? this.formatDate(endsOn) : "Açık uçlu"
      }`;
    },
    extractApiErrorMessage(error, fallbackMessage) {
      const errors = error?.response?.data?.errors;

      if (errors && typeof errors === "object") {
        const firstField = Object.keys(errors)[0];
        const firstMessage = errors[firstField]?.[0];

        if (firstMessage) {
          return firstMessage;
        }
      }

      return fallbackMessage;
    },
    handlePriorityPageUpdated(page) {
      this.priorityPage = page;
      this.load();
    },
    handlePriorityItemsPerPageUpdated(value) {
      this.priorityItemsPerPage = value;
      this.priorityPage = 1;
      this.load();
    },
    handleRiskPageUpdated(page) {
      this.riskPage = page;
      this.load();
    },
    handleRiskItemsPerPageUpdated(value) {
      this.riskItemsPerPage = value;
      this.riskPage = 1;
      this.load();
    },
    handleMonthlyPageUpdated(page) {
      this.monthlyPage = page;
      this.load();
    },
    handleBillingCandidatesItemsPerPageUpdated(value) {
      this.billingCandidatesItemsPerPage = value;
    },
    handleMonthlyItemsPerPageUpdated(value) {
      this.monthlyItemsPerPage = value;
      this.monthlyPage = 1;
      this.load();
    },
    handleYearlyPageUpdated(page) {
      this.yearlyPage = page;
      this.load();
    },
    handleYearlyItemsPerPageUpdated(value) {
      this.yearlyItemsPerPage = value;
      this.yearlyPage = 1;
      this.load();
    },
    handleYearlyTargetYearChanged(value) {
      this.yearlyTargetYear = Number(value);
      this.yearlyPage = 1;
      this.load();
    },
    handleRowActionSelect(action) {
      if (!action || !action.command) {
        return;
      }

      if (action.command === "open-reminder") {
        this.openReminderDialog(action.item);
        return;
      }

      if (action.command === "open-yearly-preview") {
        this.openYearlyPreviewDialog(action.item);
        return;
      }

      if (action.command === "open-billing-candidate-preview") {
        this.openBillingCandidatePreviewDialog(action.item);
        return;
      }

      if (action.command === "open-billing-operation") {
        this.openBillingOperationWizard(action.item);
        return;
      }

      if (action.command === "create-billing-candidate") {
        this.createBillingCandidateInvoice(action.item);
      }
    },
    handleOperationSummaryClick(item) {
      if (!item) {
        return;
      }

      this.selectedTab = item.tab;
      this.billingCandidateStatusFilter = null;

      if (item.tab === 0) {
        this.priorityFilterType = item.priorityType || null;
        this.priorityPage = 1;
        this.riskFilterType = null;
        this.riskPage = 1;
        this.load();
        return;
      }

      if (item.tab === 1) {
        this.riskFilterType = item.riskType || null;
        this.riskPage = 1;
        this.priorityFilterType = null;
        this.priorityPage = 1;
        this.load();
        return;
      }

      if (item.tab === 2) {
        this.priorityFilterType = null;
        this.priorityPage = 1;
        this.riskFilterType = null;
        this.riskPage = 1;
        this.billingInvoiceGroup = item.billingInvoiceGroup || "corrections";
        this.billingCandidateStatusFilter = item.billingCandidateStatus || null;
      }
    },
    isOperationSummaryItemActive(item) {
      if (!item || this.selectedTab !== item.tab) {
        return false;
      }

      if (item.tab === 0) {
        return this.priorityFilterType === (item.priorityType || null);
      }

      if (item.tab === 1) {
        return this.riskFilterType === (item.riskType || null);
      }

      if (item.tab === 2) {
        if (
          item.billingInvoiceGroup &&
          this.billingInvoiceGroup !== item.billingInvoiceGroup
        ) {
          return false;
        }

        if (item.billingInvoiceGroup === "corrections") {
          return (
            this.billingCandidateStatusFilter ===
            (item.billingCandidateStatus || null)
          );
        }

        return true;
      }

      return false;
    },
    clearOperationSummaryView() {
      this.priorityFilterType =
        this.selectedTab === 0 ? "overdue" : this.priorityFilterType;
      this.priorityPage = 1;
      this.riskFilterType = null;
      this.riskPage = 1;
      this.billingCandidateStatusFilter = null;
      this.load();
    },
    handleCardClick(card) {
      if (!card || !card.actionable || !card.drilldown) {
        return;
      }

      this.selectedTab = card.drilldown.tab;
      this.billingCandidateStatusFilter = null;

      if (card.drilldown.tab === 0) {
        this.priorityFilterType = card.drilldown.priorityType || "overdue";
        this.priorityPage = 1;
        this.riskFilterType = null;
        this.riskPage = 1;
      }

      if (card.drilldown.tab === 1) {
        this.riskFilterType = card.drilldown.riskType || null;
        this.riskPage = 1;
        this.priorityFilterType = null;
        this.priorityPage = 1;
      }

      if (card.drilldown.tab === 3) {
        this.priorityFilterType = null;
        this.priorityPage = 1;
        this.riskFilterType = null;
        this.riskPage = 1;
        this.monthlyPage = 1;
      }

      if (card.drilldown.tab === 4) {
        this.priorityFilterType = null;
        this.priorityPage = 1;
        this.riskFilterType = null;
        this.riskPage = 1;
        this.yearlyPage = 1;
      }

      this.load();
    },
    clearPriorityDrilldown() {
      this.priorityFilterType = "overdue";
      this.priorityPage = 1;
      this.load();
    },
    clearRiskDrilldown() {
      this.riskFilterType = null;
      this.riskPage = 1;
      this.load();
    },
    clearBillingCandidateStatusFilter() {
      this.billingCandidateStatusFilter = null;
    },
    isCardActive(card) {
      if (!card || !card.drilldown) {
        return false;
      }

      if (card.drilldown.tab === 0) {
        return (
          this.selectedTab === 0 &&
          this.priorityFilterType === (card.drilldown.priorityType || null)
        );
      }

      if (card.drilldown.tab === 1) {
        return (
          this.selectedTab === 1 &&
          this.riskFilterType === (card.drilldown.riskType || null)
        );
      }

      if (card.drilldown.tab === 3) {
        return this.selectedTab === 3;
      }

      if (card.drilldown.tab === 4) {
        return this.selectedTab === 4;
      }

      return false;
    },
    getPriorityReasonLabel(type) {
      if (type === "overdue") {
        return "Ödemesi Geciken";
      }

      if (type === "expiring_product") {
        return "Bitmeye Yakın Ürün";
      }

      if (type === "expiring_relation") {
        return "Bitmeye Yakın İlişki";
      }

      return "-";
    },
    getPriorityReasonColor(type) {
      if (type === "overdue") {
        return "error";
      }

      if (type === "expiring_product") {
        return "warning";
      }

      if (type === "expiring_relation") {
        return "secondary";
      }

      return "grey";
    },
    getBillingTypeColor(value) {
      if (value === "Aylık") {
        return "primary";
      }

      if (value === "Yıllık") {
        return "deep-purple";
      }

      if (value === "Karma") {
        return "warning";
      }

      return "grey";
    },
    isYearlyBillingType(item) {
      const billingType = String(item?.billing_type || "").toLocaleLowerCase(
        "tr-TR"
      );

      return (
        billingType.includes("yıllık") ||
        billingType.includes("yillik") ||
        billingType.includes("yearly")
      );
    },
    getPrioritySummary(item) {
      if (item.type === "overdue") {
        return `${item.overdue_invoice_count} fatura, ${this.formatCurrency(
          item.overdue_amount
        )} gecikmiş`;
      }

      if (item.type === "expiring_product") {
        return `${item.product_name || "Aktif ürün"} ${
          item.days_remaining
        } gün içinde sona eriyor`;
      }

      if (item.type === "expiring_relation") {
        return `Müşteri ilişkisi ${item.days_remaining} gün içinde sona eriyor`;
      }

      return "-";
    },
    getOverdueDayCount(item) {
      const criticalDate = this.parseDateOnly(item?.critical_date);
      const referenceDate = this.parseDateOnly(this.referenceDate);

      if (!criticalDate || !referenceDate) {
        return null;
      }

      return Math.max(
        0,
        Math.floor((referenceDate - criticalDate) / (1000 * 60 * 60 * 24))
      );
    },
    getOverdueCollectionRiskLabel(item) {
      const overdueAmount = Number(item?.overdue_amount || 0);
      const overdueDays = this.getOverdueDayCount(item);

      if (overdueAmount >= 10000) {
        return "Yüksek tutar";
      }

      if (overdueDays === null) {
        return "Takip gerekli";
      }

      if (overdueDays >= 90) {
        return "90+ gün";
      }

      if (overdueDays >= 30) {
        return "30+ gün";
      }

      return "Yeni gecikme";
    },
    getOverdueCollectionRiskColor(item) {
      const overdueAmount = Number(item?.overdue_amount || 0);
      const overdueDays = this.getOverdueDayCount(item);

      if (overdueAmount >= 10000 || overdueDays >= 90) {
        return "error";
      }

      if (overdueDays >= 30) {
        return "warning";
      }

      return "primary";
    },
    resolveRouteHref(route) {
      if (!route) {
        return null;
      }

      return this.$router.resolve(route)?.href || null;
    },
    buildNewTabRouteAction({
      key,
      label,
      icon,
      route,
      hint = null,
      disabled = false,
    }) {
      const href = this.resolveRouteHref(route);

      return {
        key,
        label,
        icon,
        href,
        hint,
        disabled: disabled || !href,
        target: "_blank",
      };
    },
    getPrimaryActionLabel(item) {
      if (item.type === "overdue") {
        return "Faturaları Gör";
      }

      if (item.type === "expiring_product") {
        return "Ürünü Düzenle";
      }

      if (item.type === "expiring_relation") {
        return "İlişkiyi Düzenle";
      }

      return "Detayı Aç";
    },
    getPrimaryActionRoute(item) {
      if (item.type === "overdue") {
        return {
          name: "admin.crm.customer-invoices.list",
          query: {
            customer_id: item.customer_id,
            is_collected: false,
          },
        };
      }

      if (item.type === "expiring_product") {
        return {
          name: "admin.crm.customers.edit",
          params: { id: item.customer_id },
          hash: "#products",
        };
      }

      if (item.type === "expiring_relation") {
        return {
          name: "admin.crm.customers.edit",
          params: { id: item.customer_id },
          hash: "#clusters",
        };
      }

      return this.getCustomerRoute(item.customer_id);
    },
    canSendReminder(item) {
      return this.getReminderAvailability(item).enabled;
    },
    getReminderAvailability(item) {
      if (item?.type !== "overdue") {
        return {
          enabled: false,
          reason:
            "Hatırlatma yalnızca ödemesi geciken kayıtlar için gönderilebilir.",
        };
      }

      if (!item.authorized_user_id) {
        return {
          enabled: false,
          reason: "Müşteri için yetkili kişi tanımlı değil.",
        };
      }

      if (!item.authorized_user_phone && !item.authorized_user_email) {
        return {
          enabled: false,
          reason: "Yetkili kişinin telefon veya e-posta bilgisi eksik.",
        };
      }

      return {
        enabled: true,
        reason: null,
      };
    },
    getPriorityMenuActions(item) {
      const actions = [
        this.buildNewTabRouteAction({
          key: `primary-${item.type}-${item.customer_id}`,
          label: this.getPrimaryActionLabel(item),
          icon: this.getPriorityPrimaryActionIcon(item),
          route: this.getPrimaryActionRoute(item),
        }),
        this.buildNewTabRouteAction({
          key: `customer-${item.customer_id}`,
          label: "Müşteriye Git",
          icon: "mdi-account-outline",
          route: this.getCustomerRoute(item.customer_id),
        }),
      ];

      if (item.type === "overdue") {
        const reminderAvailability = this.getReminderAvailability(item);

        actions.splice(1, 0, {
          key: `reminder-${item.type}-${item.customer_id}`,
          label: "Hatırlatma Gönder",
          icon: "mdi-message-alert-outline",
          disabled: !reminderAvailability.enabled,
          hint: reminderAvailability.reason,
          command: "open-reminder",
          item,
        });
      }

      return actions;
    },
    getMonthlyProductParts(item) {
      const label = String(item?.current_product_label || "").trim();

      if (!label || label === "-") {
        return {
          name: "-",
          amount: "",
        };
      }

      const [name, ...amountParts] = label.split("•");

      return {
        name: String(name || "").trim() || "-",
        amount: amountParts.join("•").trim(),
      };
    },
    getMonthlyProductName(item) {
      return this.getMonthlyProductParts(item).name;
    },
    getMonthlyProductAmountLabel(item) {
      const { amount, name } = this.getMonthlyProductParts(item);

      if (amount) {
        return amount.includes("/ ay") ? amount : `${amount} / ay`;
      }

      if (name === "Karma ürün") {
        return "Birden fazla aktif ürün kaydı";
      }

      if (name === "-") {
        return "Aylık ürün bilgisi yok";
      }

      return "Aylık standart ürün";
    },
    getMonthlyPeriodHint(item) {
      const periodLabel = String(item?.current_period_label || "").trim();
      const normalizedPeriodLabel = periodLabel.toLocaleLowerCase("tr-TR");

      if (!periodLabel || periodLabel === "-") {
        return "Dönem bilgisi yok";
      }

      if (normalizedPeriodLabel.includes("karma")) {
        return "Ürün dönemleri ayrışıyor";
      }

      if (
        normalizedPeriodLabel.includes("açık uçlu") ||
        normalizedPeriodLabel.includes("acik uclu")
      ) {
        return "Aktif ürün dönemi";
      }

      return "Bitişli aktif dönem";
    },
    isMonthlyOpenEnded(item) {
      const periodLabel = String(
        item?.current_period_label || ""
      ).toLocaleLowerCase("tr-TR");

      return (
        periodLabel.includes("açık uçlu") || periodLabel.includes("acik uclu")
      );
    },
    isMonthlyFinitePeriod(item) {
      const periodLabel = String(item?.current_period_label || "").trim();

      return (
        Boolean(periodLabel) &&
        periodLabel !== "-" &&
        !this.isMonthlyOpenEnded(item) &&
        !periodLabel.toLocaleLowerCase("tr-TR").includes("karma")
      );
    },
    isMonthlyAttentionNeeded(item) {
      const note = String(item?.note || "").trim();

      return (
        Boolean(item?.setup_tab) ||
        Boolean(note && note !== "Aktif aylık portföy")
      );
    },
    getMonthlyStatusLabel(item) {
      return this.isMonthlyAttentionNeeded(item) ? "Kontrol" : "Sağlıklı";
    },
    getMonthlyStatusColor(item) {
      return this.isMonthlyAttentionNeeded(item) ? "warning" : "success";
    },
    getMonthlyPrimaryActionLabel(item) {
      if (item?.setup_tab === "products") {
        return "Ürün Sekmesine Git";
      }

      if (item?.setup_tab === "clusters") {
        return "İlişki Sekmesine Git";
      }

      return "Müşteriye Git";
    },
    getMonthlyPrimaryActionRoute(item) {
      if (item?.setup_tab === "products") {
        return {
          name: "admin.crm.customers.edit",
          params: { id: item.customer_id },
          hash: "#products",
        };
      }

      if (item?.setup_tab === "clusters") {
        return {
          name: "admin.crm.customers.edit",
          params: { id: item.customer_id },
          hash: "#clusters",
        };
      }

      return this.getCustomerRoute(item.customer_id);
    },
    getMonthlyPrimaryActionIcon(item) {
      if (item?.setup_tab === "products") {
        return "mdi-package-variant-closed";
      }

      if (item?.setup_tab === "clusters") {
        return "mdi-source-branch";
      }

      return "mdi-account-outline";
    },
    getMonthlyMenuActions(item) {
      const actions = [
        this.buildNewTabRouteAction({
          key: `monthly-primary-${item.customer_id}`,
          label: this.getMonthlyPrimaryActionLabel(item),
          icon: this.getMonthlyPrimaryActionIcon(item),
          route: this.getMonthlyPrimaryActionRoute(item),
        }),
      ];

      if (item?.setup_tab !== "products") {
        actions.push(
          this.buildNewTabRouteAction({
            key: `monthly-products-${item.customer_id}`,
            label: "Ürün Sekmesine Git",
            icon: "mdi-package-variant-closed",
            route: {
              name: "admin.crm.customers.edit",
              params: { id: item.customer_id },
              hash: "#products",
            },
          })
        );
      }

      if (item?.setup_tab !== "clusters") {
        actions.push(
          this.buildNewTabRouteAction({
            key: `monthly-clusters-${item.customer_id}`,
            label: "İlişki Sekmesine Git",
            icon: "mdi-source-branch",
            route: {
              name: "admin.crm.customers.edit",
              params: { id: item.customer_id },
              hash: "#clusters",
            },
          })
        );
      }

      return actions;
    },
    formatBillingCandidateClusterLabel(item) {
      const labelList = Array.from(
        new Set(
          (item?.items_preview || [])
            .map((line) => line.cluster_label)
            .filter(Boolean)
        )
      );

      if (labelList.length === 0) {
        return "-";
      }

      if (labelList.length === 1) {
        return labelList[0];
      }

      return `${labelList[0]} +${labelList.length - 1} TYA`;
    },
    getBillingCandidateStatusLabel(status) {
      if (status === "ready") {
        return "Hazır";
      }

      if (status === "scheduled") {
        return "Otomatik Kesilecek";
      }

      return "-";
    },
    getBillingCandidateStatusFilterLabel(status) {
      if (status === "ready") {
        return "Şimdi oluşturulabilir faturalar";
      }

      if (status === "scheduled") {
        return "Otomatik kesilecek faturalar";
      }

      return "Düzeltme faturaları";
    },
    getBillingCandidateStatusColor(status) {
      if (status === "ready") {
        return "success";
      }

      if (status === "scheduled") {
        return "teal";
      }

      return "grey";
    },
    getBillingCandidateKey(item) {
      if (!item || !item.customer_id || !item.reference_run_date) {
        return null;
      }

      return `${item.customer_id}:${item.reference_run_date}`;
    },
    isBillingCandidateCreating(item) {
      const key = this.getBillingCandidateKey(item);

      return Boolean(key && this.creatingBillingCandidateMap[key]);
    },
    setBillingCandidateCreating(item, value) {
      const key = this.getBillingCandidateKey(item);

      if (!key) {
        return;
      }

      if (value) {
        this.$set(this.creatingBillingCandidateMap, key, true);
        return;
      }

      this.$delete(this.creatingBillingCandidateMap, key);
    },
    getBillingCandidateMenuActions(item) {
      const isCreating = this.isBillingCandidateCreating(item);
      const isCreateLocked = this.billingCandidateCreateApplying && !isCreating;
      const candidateKey =
        this.getBillingCandidateKey(item) || `customer-${item.customer_id}`;
      let createHint = null;

      if (isCreating) {
        createHint = "Fatura oluşturuluyor.";
      } else if (isCreateLocked) {
        createHint = "Başka bir fatura oluşturuluyor.";
      } else if (item.status !== "ready") {
        createHint = `${this.formatDate(
          item.reference_run_date
        )} tarihinde oluşturulabilir.`;
      }

      const actions = [
        {
          key: `billing-candidate-preview-${candidateKey}`,
          label: "Önizleme",
          icon: "mdi-eye-outline",
          command: "open-billing-candidate-preview",
          item,
        },
        {
          key: `billing-candidate-create-${candidateKey}`,
          label: isCreating ? "Oluşturuluyor..." : "Oluştur",
          icon: isCreating ? "mdi-loading" : "mdi-file-document-plus-outline",
          loading: isCreating,
          disabled: item.status !== "ready" || isCreating || isCreateLocked,
          hint: createHint,
          command: "create-billing-candidate",
          item,
        },
        this.buildNewTabRouteAction({
          key: `billing-candidate-customer-${item.customer_id}`,
          label: "Müşteriye Git",
          icon: "mdi-account-outline",
          route: this.getCustomerRoute(item.customer_id),
        }),
      ];

      return actions;
    },
    getCustomerRoute(customerId) {
      return {
        name: "admin.crm.customers.edit",
        params: { id: customerId },
      };
    },
    getYearEndTransitionSnapshotDate() {
      return `${this.yearlyTargetYear - 1}-12-31`;
    },
    getDefaultReminderChannel(item) {
      if (item?.authorized_user_phone) {
        return 2;
      }

      if (item?.authorized_user_email) {
        return 4;
      }

      return null;
    },
    buildReminderSubject() {
      return "Ödeme Hatırlatması";
    },
    buildReminderBody(item) {
      return `${this.formatCurrency(
        item?.overdue_amount
      )} tutarında gecikmiş faturanız bulunmaktadır. Ödemenizi zamanında yapmanızı rica ederiz.`;
    },
    openReminderDialog(item) {
      const reminderAvailability = this.getReminderAvailability(item);

      if (!reminderAvailability.enabled) {
        this.$toast.warning(reminderAvailability.reason);
        return;
      }

      const defaultChannel = this.getDefaultReminderChannel(item);
      if (!defaultChannel) {
        this.$toast.warning(
          "Muhatap kişi için SMS veya e-posta bilgisi bulunamadı."
        );
        return;
      }

      this.reminderDialog.target = item;
      this.reminderDialog.form.notification_channel_id = defaultChannel;
      this.reminderDialog.form.subject = this.buildReminderSubject();
      this.reminderDialog.form.body = this.buildReminderBody(item);
      this.reminderDialog.open = true;
    },
    openYearlyPreviewDialog(item) {
      if (!item) {
        return;
      }

      this.yearlyPreviewDialog.loading = true;
      this.yearlyPreviewDialog.applying = false;
      this.yearlyPreviewDialog.item = item;
      this.yearlyPreviewDialog.preview = null;
      this.yearlyPreviewDialog.open = true;

      this.$api
        .query("api-super-admin/customers/portfolio-summary/yearly-preview", {
          params: {
            customer_id: item.customer_id,
            target_year: item.target_year,
          },
        })
        .then((response) => {
          this.yearlyPreviewDialog.preview = response?.data?.data || null;
        })
        .catch((error) => {
          this.handleYearlyPreviewError(error);
          this.closeYearlyPreviewDialog(true);
        })
        .finally(() => {
          this.yearlyPreviewDialog.loading = false;
        });
    },
    closeYearlyPreviewDialog(force = false) {
      if (this.yearlyPreviewDialog.applying && !force) {
        return;
      }

      this.yearlyPreviewDialog.open = false;
      this.yearlyPreviewDialog.loading = false;
      this.yearlyPreviewDialog.applying = false;
      this.yearlyPreviewDialog.item = null;
      this.yearlyPreviewDialog.preview = null;
    },
    openBillingCandidatePreviewDialog(item) {
      if (!item) {
        return;
      }

      this.billingCandidatePreviewDialog.item = item;
      this.billingCandidatePreviewDialog.open = true;
    },
    closeBillingCandidatePreviewDialog(force = false) {
      if (
        this.billingCandidatePreviewDialog.item &&
        this.isBillingCandidateCreating(
          this.billingCandidatePreviewDialog.item
        ) &&
        !force
      ) {
        return;
      }

      this.billingCandidatePreviewDialog.open = false;
      this.billingCandidatePreviewDialog.applying = false;
      this.billingCandidatePreviewDialog.item = null;
    },
    openBillingOperationWizard(item) {
      const operationType = this.getBillingOperationTypeForIssue(item);

      if (!operationType) {
        return;
      }

      this.billingOperationWizard.source =
        this.buildBillingOperationSourceFromIssue(item);
      this.billingOperationWizard.operationType = operationType;
      this.billingOperationWizard.open = true;
    },
    handleBillingOperationApplied() {
      this.load();
      this.loadBillingCandidates();
    },
    getBillingOperationTypeForIssue(item) {
      const operationType = getOperationTypeForIssue(item?.type);

      if (!isWizardOperationSupported(operationType)) {
        return null;
      }

      return operationType;
    },
    buildBillingOperationSourceFromIssue(item) {
      return {
        type: "portfolio_issue",
        issue_type: item.type,
        customer_id: item.customer_id,
        customer_name: item.customer_name,
        cluster_id: item.cluster_id,
        cluster_name: item.cluster_name,
        relation_id: item.relation_id,
      };
    },
    closeReminderDialog(force = false) {
      if (this.reminderDialog.sending && !force) {
        return;
      }

      this.reminderDialog.open = false;
      this.reminderDialog.target = null;
      this.reminderDialog.form.notification_channel_id = null;
      this.reminderDialog.form.subject = "";
      this.reminderDialog.form.body = "";
    },
    handleReminderError(error) {
      if (!error || !error.response || !error.response.status) {
        throw error;
      }

      if (error.response.status === 422) {
        this.$toast.error(
          "Hatırlatma gönderimi için gerekli iletişim bilgileri eksik."
        );
      } else if (error.response.status === 403) {
        this.$toast.error("Bu işlemi yapmaya yetkili değilsiniz.");
      } else if (error.response.status === 500) {
        this.$toast.error("Sunucu hatası. Hatırlatma gönderilemedi.");
      } else {
        this.$toast.error("Hatırlatma gönderilemedi.");
      }
    },
    handleYearlyPreviewError(error) {
      if (!error || !error.response || !error.response.status) {
        throw error;
      }

      if (error.response.status === 422) {
        this.$toast.error(
          this.extractApiErrorMessage(
            error,
            "Yıllık fatura önizlemesi oluşturulamadı."
          )
        );
      } else if (error.response.status === 403) {
        this.$toast.error("Bu işlemi yapmaya yetkili değilsiniz.");
      } else if (error.response.status === 500) {
        this.$toast.error("Sunucu hatası. Önizleme oluşturulamadı.");
      } else {
        this.$toast.error("Yıllık fatura önizlemesi oluşturulamadı.");
      }
    },
    handleYearlyApplyError(error) {
      if (!error || !error.response || !error.response.status) {
        throw error;
      }

      if (error.response.status === 422) {
        this.$toast.error(
          this.extractApiErrorMessage(error, "Yıllık fatura oluşturulamadı.")
        );
      } else if (error.response.status === 403) {
        this.$toast.error("Bu işlemi yapmaya yetkili değilsiniz.");
      } else if (error.response.status === 500) {
        this.$toast.error("Sunucu hatası. Yıllık fatura oluşturulamadı.");
      } else {
        this.$toast.error("Yıllık fatura oluşturulamadı.");
      }
    },
    handleBillingCandidateError(error) {
      if (!error || !error.response || !error.response.status) {
        throw error;
      }

      if (error.response.status === 422) {
        this.$toast.error(
          this.extractApiErrorMessage(
            error,
            "Otomatik kesilecek faturalar yüklenemedi."
          )
        );
      } else if (error.response.status === 403) {
        this.$toast.error("Bu işlemi yapmaya yetkili değilsiniz.");
      } else if (error.response.status === 500) {
        this.$toast.error(
          "Sunucu hatası. Otomatik kesilecek faturalar yüklenemedi."
        );
      } else {
        this.$toast.error("Otomatik kesilecek faturalar yüklenemedi.");
      }
    },
    handleBillingCandidateCreateError(error) {
      if (!error || !error.response || !error.response.status) {
        throw error;
      }

      if (error.response.status === 422) {
        this.$toast.error(
          this.extractApiErrorMessage(error, "Otomatik fatura oluşturulamadı.")
        );
      } else if (error.response.status === 403) {
        this.$toast.error("Bu işlemi yapmaya yetkili değilsiniz.");
      } else if (error.response.status === 500) {
        this.$toast.error("Sunucu hatası. Otomatik fatura oluşturulamadı.");
      } else {
        this.$toast.error("Otomatik fatura oluşturulamadı.");
      }
    },
    sendReminder() {
      if (this.reminderDialog.sending || !this.reminderDialog.target) {
        return;
      }

      if (!this.$refs.reminderForm.validate()) {
        this.$toast.error("Lütfen gerekli alanları doldurun.");
        return;
      }

      const payload = {
        user_ids: [this.reminderDialog.target.authorized_user_id],
        notification_channel_id:
          this.reminderDialog.form.notification_channel_id,
        body: this.reminderDialog.form.body,
        subject: this.reminderDialog.form.subject || null,
      };

      this.reminderDialog.sending = true;

      this.$api
        .post("api-super-admin/notifications/send", payload)
        .then(() => {
          this.reminderDialog.sending = false;
          this.$toast.success("Hatırlatma gönderimi için batch oluşturuldu.");
          this.closeReminderDialog(true);
        })
        .catch((error) => {
          this.handleReminderError(error);
        })
        .finally(() => {
          this.reminderDialog.sending = false;
        });
    },
    applyYearlyPreviewAction() {
      if (
        this.yearlyPreviewDialog.applying ||
        !this.yearlyPreviewDialog.preview ||
        !this.yearlyPreviewDialog.item
      ) {
        return;
      }

      this.yearlyPreviewDialog.applying = true;

      this.$api
        .post("api-super-admin/customers/portfolio-summary/yearly-apply", {
          customer_id: this.yearlyPreviewDialog.item.customer_id,
          target_year: this.yearlyPreviewDialog.item.target_year,
        })
        .then((response) => {
          const invoice = response?.data?.data;
          const invoiceId = invoice?.id;

          this.$toast.success("Yıllık fatura oluşturuldu.");
          this.closeYearlyPreviewDialog(true);
          this.load();

          if (invoiceId) {
            this.$router.push({
              name: "admin.crm.customer-invoices.edit",
              params: { id: invoiceId },
            });
          }
        })
        .catch((error) => {
          this.handleYearlyApplyError(error);
        })
        .finally(() => {
          this.yearlyPreviewDialog.applying = false;
        });
    },
    createBillingCandidateInvoice(item) {
      if (
        !item ||
        this.billingCandidateCreateApplying ||
        this.isBillingCandidateCreating(item) ||
        item.status !== "ready"
      ) {
        return;
      }

      this.billingCandidateCreateApplying = true;
      this.billingCandidatePreviewDialog.applying = true;
      this.setBillingCandidateCreating(item, true);

      this.$api
        .post("api-super-admin/customer-invoices/billing-candidates/create", {
          customer_id: item.customer_id,
          reference_run_date: item.reference_run_date,
        })
        .then(() => {
          this.$toast.success("Fatura oluşturuldu.");
          this.closeBillingCandidatePreviewDialog(true);
          this.load();
        })
        .catch((error) => {
          this.handleBillingCandidateCreateError(error);
        })
        .finally(() => {
          this.setBillingCandidateCreating(item, false);
          this.billingCandidateCreateApplying = false;
          this.billingCandidatePreviewDialog.applying = false;
        });
    },
    getRiskTypeLabel(type) {
      if (
        type === "missing_both" ||
        type === "missing_tax_number" ||
        type === "invalid_tax_number" ||
        type === "overlapping_customer_relation"
      ) {
        return "Kritik";
      }

      return "Riskli";
    },
    getRiskTypeColor(type) {
      if (type === "missing_both") {
        return "error";
      }

      if (type === "missing_tax_number") {
        return "error";
      }

      if (type === "invalid_tax_number") {
        return "error";
      }

      if (type === "overlapping_customer_relation") {
        return "error";
      }

      if (type === "missing_billing_profile") {
        return "warning";
      }

      if (type === "ended_relation") {
        return "warning";
      }

      if (type === "missing_relation") {
        return "warning";
      }

      if (type === "ended_product") {
        return "secondary";
      }

      if (type === "missing_product") {
        return "secondary";
      }

      if (type === "missing_authorized_user") {
        return "secondary";
      }

      return "grey";
    },
    getRiskIssueLabel(type) {
      if (type === "ended_relation") {
        return "Bitmiş Müşteri İlişkisi";
      }

      if (type === "ended_product") {
        return "Bitmiş Ürün";
      }

      if (type === "missing_product") {
        return "Eksik Ürün";
      }

      if (type === "missing_relation") {
        return "Eksik Müşteri İlişkisi";
      }

      if (type === "missing_both") {
        return "Eksik Ürün ve Müşteri İlişkisi";
      }

      if (type === "overlapping_customer_relation") {
        return "Çakışan Müşteri İlişkisi";
      }

      if (type === "missing_tax_number") {
        return "VKN / TCKN Eksik";
      }

      if (type === "invalid_tax_number") {
        return "VKN / TCKN Geçersiz";
      }

      if (type === "missing_billing_profile") {
        return "Eksik Fatura Profili";
      }

      if (type === "missing_authorized_user") {
        return "Yetkili Kişi Yok";
      }

      return "-";
    },
    getRiskIssueHint(type) {
      if (type === "missing_both") {
        return "Kurulum tamamlanmamış";
      }

      if (type === "overlapping_customer_relation") {
        return "Fatura sahibi belirsiz";
      }

      if (type === "ended_relation") {
        return "İlişki dönemi kapanmış";
      }

      if (type === "missing_relation") {
        return "Müşteri ilişkisi yok";
      }

      if (type === "ended_product") {
        return "Ürün dönemi kapanmış";
      }

      if (type === "missing_product") {
        return "Standart ürün yok";
      }

      if (type === "missing_tax_number") {
        return "Vergi kimliği yok";
      }

      if (type === "invalid_tax_number") {
        return "Vergi kimliği doğrulanamadı";
      }

      if (type === "missing_billing_profile") {
        return "Fatura bilgileri eksik";
      }

      if (type === "missing_authorized_user") {
        return "İletişim muhatabı yok";
      }

      return "Kontrol gerekli";
    },
    getRiskResolutionLabel(itemOrType) {
      const type =
        typeof itemOrType === "string" ? itemOrType : itemOrType?.type;

      if (type === "missing_both") {
        return "İlişki ve standart ürün kurulmalı.";
      }

      if (type === "overlapping_customer_relation") {
        return "Aktif kalacak ilişki seçilmeli.";
      }

      if (type === "ended_relation") {
        return "Müşteri ilişkisi uzatılmalı.";
      }

      if (type === "missing_relation") {
        return "Müşteri ilişkisi eklenmeli.";
      }

      if (type === "ended_product") {
        return "Ürün dönemi uzatılmalı.";
      }

      if (type === "missing_product") {
        return "Standart ürün eklenmeli.";
      }

      if (type === "missing_tax_number") {
        return "VKN / TCKN tamamlanmalı.";
      }

      if (type === "invalid_tax_number") {
        return "VKN / TCKN düzeltilmeli.";
      }

      if (type === "missing_billing_profile") {
        return "Fatura profili tamamlanmalı.";
      }

      if (type === "missing_authorized_user") {
        return "Yetkili kişi atanmalı.";
      }

      return "Kayıt kontrol edilmeli.";
    },
    getRiskImpactLabel(itemOrType) {
      const type =
        typeof itemOrType === "string" ? itemOrType : itemOrType?.type;

      if (type === "missing_both") {
        return "Kurulum tamamlanmadan faturalama başlayamaz";
      }

      if (type === "missing_tax_number") {
        return "Faturalama kimliği eksik, resmileştirme ve entegrasyon riski var";
      }

      if (type === "invalid_tax_number") {
        return "Faturalama kimliği geçersiz, resmileştirme ve entegrasyon riski var";
      }

      if (type === "overlapping_customer_relation") {
        const currentCustomerName =
          typeof itemOrType === "object"
            ? itemOrType.current_customer_name
            : null;

        if (currentCustomerName) {
          return `Aktif fatura sahibi ${currentCustomerName}; bu ilişki aynı dönemde açık kalmış`;
        }

        return "Aynı TYA aynı dönemde birden fazla müşteriye bağlı; fatura muhatabı belirsiz";
      }

      if (type === "missing_billing_profile") {
        return "Adres, vergi dairesi veya lokasyon bilgisi eksik; Paraşüt senkronu riski var";
      }

      if (type === "ended_relation") {
        return "Önceki müşteri ilişkisi bitmiş; müşteri ilişkisi uzatılmadan faturalanamaz";
      }

      if (type === "missing_authorized_user") {
        return "Hatırlatma ve müşteri iletişimi gönderilemez";
      }

      if (type === "ended_product") {
        return "Önceki ürün dönemi bitmiş; ürün uzatılmadan faturalama devam etmez";
      }

      return "Faturalama bloklu";
    },
    getRiskPrimaryActionLabel(item) {
      if (item.type === "ended_relation") {
        return "Müşteri İlişkisini Devam Ettir";
      }

      if (item.type === "ended_product") {
        return "Ürünü Uzat";
      }

      if (item.type === "missing_product") {
        return "Ürün Ekle";
      }

      if (item.type === "missing_relation") {
        return "Müşteri İlişkisi Ekle";
      }

      if (item.type === "missing_both") {
        return "İlişki ve Ürün Ekle";
      }

      if (item.type === "overlapping_customer_relation") {
        return "Çakışmayı Çöz";
      }

      if (item.type === "missing_tax_number") {
        return "VKN / TCKN Tamamla";
      }

      if (item.type === "invalid_tax_number") {
        return "VKN / TCKN Düzelt";
      }

      if (item.type === "missing_billing_profile") {
        return "Bilgileri Tamamla";
      }

      if (item.type === "missing_authorized_user") {
        return "Yetkili Kişi Ata";
      }

      return "Detayı Aç";
    },
    getRiskPrimaryActionRoute(item) {
      if (item.type === "ended_product") {
        return {
          name: "admin.crm.customers.edit",
          params: { id: item.customer_id },
          hash: "#products",
        };
      }

      if (item.type === "missing_product") {
        return {
          name: "admin.crm.customers.edit",
          params: { id: item.customer_id },
          hash: "#products",
        };
      }

      if (item.type === "missing_authorized_user") {
        return {
          name: "admin.crm.customers.edit",
          params: { id: item.customer_id },
          hash: "#info",
        };
      }

      if (item.type === "missing_tax_number") {
        return {
          name: "admin.crm.customers.edit",
          params: { id: item.customer_id },
          hash: "#info",
        };
      }

      if (item.type === "invalid_tax_number") {
        return {
          name: "admin.crm.customers.edit",
          params: { id: item.customer_id },
          hash: "#info",
        };
      }

      if (item.type === "missing_billing_profile") {
        return {
          name: "admin.crm.customers.edit",
          params: { id: item.customer_id },
          hash: "#info",
        };
      }

      return {
        name: "admin.crm.customers.edit",
        params: { id: item.customer_id },
        hash: "#clusters",
      };
    },
    getRiskMenuActions(item) {
      const actions = [];

      const operationType = this.getBillingOperationTypeForIssue(item);

      if (operationType) {
        actions.push({
          key: `operation-${item.type}-${item.customer_id}-${item.cluster_id}`,
          label: "Çöz",
          icon: "mdi-wrench-outline",
          command: "open-billing-operation",
          item,
        });
      }

      actions.push(
        this.buildNewTabRouteAction({
          key: `primary-${item.type}-${item.customer_id}`,
          label: this.getRiskPrimaryActionLabel(item),
          icon: this.getRiskPrimaryActionIcon(item),
          route: this.getRiskPrimaryActionRoute(item),
        })
      );

      actions.push(
        this.buildNewTabRouteAction({
          key: `customer-${item.customer_id}`,
          label: "Müşteriye Git",
          icon: "mdi-account-outline",
          route: this.getCustomerRoute(item.customer_id),
        })
      );

      return actions;
    },
    getPriorityPrimaryActionIcon(item) {
      if (item.type === "overdue") {
        return "mdi-file-document-outline";
      }

      if (item.type === "expiring_product") {
        return "mdi-package-variant-closed";
      }

      if (item.type === "expiring_relation") {
        return "mdi-source-branch";
      }

      return "mdi-open-in-new";
    },
    getRiskPrimaryActionIcon(item) {
      if (item.type === "ended_relation") {
        return "mdi-source-branch";
      }

      if (item.type === "ended_product") {
        return "mdi-package-variant";
      }

      if (item.type === "missing_product") {
        return "mdi-package-variant-plus";
      }

      if (item.type === "missing_relation") {
        return "mdi-source-branch-plus";
      }

      if (item.type === "missing_both") {
        return "mdi-wrench-outline";
      }

      if (item.type === "overlapping_customer_relation") {
        return "mdi-source-branch-sync";
      }

      if (item.type === "missing_tax_number") {
        return "mdi-card-account-details-outline";
      }

      if (item.type === "invalid_tax_number") {
        return "mdi-card-account-details-outline";
      }

      if (item.type === "missing_billing_profile") {
        return "mdi-file-document-edit-outline";
      }

      if (item.type === "missing_authorized_user") {
        return "mdi-account-plus-outline";
      }

      return "mdi-open-in-new";
    },
    getRiskRowClass(item) {
      if (item.type === "ended_relation") {
        return "portfolio-summary__risk-row portfolio-summary__risk-row--warning";
      }

      if (item.type === "ended_product") {
        return "portfolio-summary__risk-row portfolio-summary__risk-row--soft";
      }

      if (item.type === "missing_both") {
        return "portfolio-summary__risk-row portfolio-summary__risk-row--danger";
      }

      if (item.type === "missing_tax_number") {
        return "portfolio-summary__risk-row portfolio-summary__risk-row--danger";
      }

      if (item.type === "invalid_tax_number") {
        return "portfolio-summary__risk-row portfolio-summary__risk-row--danger";
      }

      if (item.type === "overlapping_customer_relation") {
        return "portfolio-summary__risk-row portfolio-summary__risk-row--danger";
      }

      if (item.type === "missing_billing_profile") {
        return "portfolio-summary__risk-row portfolio-summary__risk-row--warning";
      }

      if (item.type === "missing_relation") {
        return "portfolio-summary__risk-row portfolio-summary__risk-row--warning";
      }

      if (item.type === "missing_product") {
        return "portfolio-summary__risk-row portfolio-summary__risk-row--soft";
      }

      if (item.type === "missing_authorized_user") {
        return "portfolio-summary__risk-row portfolio-summary__risk-row--soft";
      }

      return "";
    },
    getYearlyProductParts(item) {
      const label = String(item?.current_product_label || "").trim();

      if (!label || label === "-") {
        return {
          name: "-",
          amount: "",
        };
      }

      const [name, ...amountParts] = label.split("•");

      return {
        name: String(name || "").trim() || "-",
        amount: amountParts.join("•").trim(),
      };
    },
    getYearlyProductName(item) {
      return this.getYearlyProductParts(item).name;
    },
    getYearlyProductAmountLabel(item) {
      const { amount, name } = this.getYearlyProductParts(item);

      if (amount) {
        return amount.includes("/ yıl") ? amount : `${amount} / yıl`;
      }

      if (name === "Karma ürün") {
        return "Birden fazla aktif yıllık ürün kaydı";
      }

      if (name === "-") {
        return "Yıllık ürün bilgisi yok";
      }

      return "Yıllık standart ürün";
    },
    getYearlyActivePeriodLabel(item) {
      return item?.current_coverage_label || item?.current_period_label || "-";
    },
    isYearlyCalendarYear(item) {
      const startsOn = String(item?.current_coverage_starts_on || "");
      const endsOn = String(item?.current_coverage_ends_on || "");

      if (!startsOn || !endsOn) {
        return false;
      }

      return startsOn.endsWith("-01-01") && endsOn.endsWith("-12-31");
    },
    isYearlyAttentionNeeded(item) {
      if (item?.reason_code === "already_covered_for_target_year") {
        return false;
      }

      return item?.status === "not_ready";
    },
    isYearlyOffCycle(item) {
      return (
        !this.isYearlyAttentionNeeded(item) && !this.isYearlyCalendarYear(item)
      );
    },
    getYearlyPeriodHint(item) {
      if (this.isYearlyAttentionNeeded(item)) {
        return "Aktif dönem doğrulanamıyor";
      }

      if (this.isYearlyCalendarYear(item)) {
        return "Takvim yılına hizalı";
      }

      return "Ara dönemli yıllık kayıt";
    },
    getYearlyInventoryLabel(item) {
      if (this.isYearlyAttentionNeeded(item)) {
        return "Kontrol";
      }

      if (this.isYearlyOffCycle(item)) {
        return "Ara dönemli";
      }

      return "Sağlıklı";
    },
    getYearlyInventoryColor(item) {
      if (this.isYearlyAttentionNeeded(item)) {
        return "warning";
      }

      if (this.isYearlyOffCycle(item)) {
        return "deep-purple";
      }

      return "success";
    },
    getYearlyInventoryNote(item) {
      if (this.isYearlyAttentionNeeded(item)) {
        return item?.reason || "Kontrol gerekli";
      }

      if (this.isYearlyOffCycle(item)) {
        return "Yenileme aracı hedef yıl aksiyonunu ayrıca hesaplar";
      }

      return "Yıllık portföy kaydı aktif";
    },
    getYearlyStatusLabel(status) {
      if (status === "ready") {
        return "Hazır";
      }

      if (status === "bridge_required") {
        return "Köprü Gerekli";
      }

      if (status === "not_ready") {
        return "Hazır Değil";
      }

      return "-";
    },
    getYearlyStatusColor(status) {
      if (status === "ready") {
        return "success";
      }

      if (status === "bridge_required") {
        return "deep-purple";
      }

      if (status === "not_ready") {
        return "warning";
      }

      return "grey";
    },
    getYearlyActionTypeLabel(actionType) {
      if (actionType === "renewal") {
        return "Normal Yenileme";
      }

      if (actionType === "bridge") {
        return "Köprü Faturası";
      }

      return "-";
    },
    getYearlyApplyButtonLabel(actionType) {
      if (actionType === "renewal") {
        return "Yenilemeyi Oluştur";
      }

      if (actionType === "bridge") {
        return "Köprüyü Oluştur";
      }

      return "Uygula";
    },
    getYearlySetupRoute(item) {
      return {
        name: "admin.crm.customers.edit",
        params: { id: item.customer_id },
        hash: item?.setup_tab ? `#${item.setup_tab}` : "#info",
      };
    },
    getYearlyMenuActions(item) {
      const actions = [];

      if (item.status === "not_ready") {
        actions.push(
          this.buildNewTabRouteAction({
            key: `yearly-setup-${item.customer_id}`,
            label: "Kuruluma Git",
            icon: "mdi-wrench-outline",
            route: this.getYearlySetupRoute(item),
          })
        );
      } else {
        actions.push({
          key: `yearly-preview-${item.customer_id}`,
          label: "Önizleme",
          icon: "mdi-eye-outline",
          command: "open-yearly-preview",
          item,
        });
      }

      actions.push(
        this.buildNewTabRouteAction({
          key: `yearly-customer-${item.customer_id}`,
          label: "Müşteriye Git",
          icon: "mdi-account-outline",
          route: this.getCustomerRoute(item.customer_id),
        })
      );

      return actions;
    },
  },
};
</script>

<style scoped>
.portfolio-summary__context-chip {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 4px 14px;
  border: 1px solid rgba(60, 60, 60, 0.12);
  border-radius: 999px;
  background: #ffffff;
  color: rgba(0, 0, 0, 0.54);
  font-size: 13px;
  line-height: 1.2;
}

.portfolio-summary__context-chip strong {
  margin-left: 4px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 600;
}

.portfolio-summary__root-card {
  position: relative;
  overflow: hidden;
}

.portfolio-summary__header {
  display: flex;
  gap: 16px;
}

.portfolio-summary__header-copy {
  min-width: 0;
  flex: 1 1 auto;
}

.portfolio-summary__header-actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  justify-content: flex-end;
}

.portfolio-summary__operation-strip {
  overflow: hidden;
  background: #ffffff;
}

.portfolio-summary__operation-strip-header {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(60, 60, 60, 0.12);
}

.portfolio-summary__operation-strip-title {
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
  font-weight: 700;
}

.portfolio-summary__operation-items {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.portfolio-summary__operation-item {
  position: relative;
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  min-height: 116px;
  padding: 16px;
  border: 0;
  border-right: 1px solid rgba(60, 60, 60, 0.12);
  background: #ffffff;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.18s ease, box-shadow 0.18s ease;
}

.portfolio-summary__operation-item:last-child {
  border-right: 0;
}

.portfolio-summary__operation-item::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  background: #3699ff;
}

.portfolio-summary__operation-item:hover,
.portfolio-summary__operation-item:focus {
  outline: none;
  background: #fafbff;
}

.portfolio-summary__operation-item--active {
  background: #f7f8ff;
  box-shadow: inset 0 0 0 1px rgba(54, 153, 255, 0.24);
}

.portfolio-summary__operation-item--danger::before {
  background: #f64e60;
}

.portfolio-summary__operation-item--ready::before {
  background: #1bc5bd;
}

.portfolio-summary__operation-item--scheduled::before {
  background: #ffa800;
}

.portfolio-summary__operation-item--overdue::before {
  background: #f64e60;
}

.portfolio-summary__operation-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: #f3f6f9;
}

.portfolio-summary__operation-item--danger .portfolio-summary__operation-icon,
.portfolio-summary__operation-item--overdue .portfolio-summary__operation-icon {
  background: #fff4f4;
  color: #f64e60;
}

.portfolio-summary__operation-item--ready .portfolio-summary__operation-icon {
  background: #e8fff3;
  color: #1bc5bd;
}

.portfolio-summary__operation-item--scheduled
  .portfolio-summary__operation-icon {
  background: #fff8dd;
  color: #ffa800;
}

.portfolio-summary__operation-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.portfolio-summary__operation-title {
  margin-bottom: 4px;
  color: rgba(0, 0, 0, 0.87);
  font-size: 13px;
  font-weight: 700;
}

.portfolio-summary__operation-caption {
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  line-height: 1.35;
}

.portfolio-summary__operation-link {
  margin-top: 8px;
  color: #3699ff;
  font-size: 12px;
  font-weight: 600;
}

.portfolio-summary__operation-count {
  color: rgba(0, 0, 0, 0.87);
  font-size: 26px;
  font-weight: 700;
  line-height: 1;
}

.portfolio-summary__kpi-section-header {
  gap: 12px;
  margin-bottom: 10px;
}

.portfolio-summary__kpi-section-title {
  color: rgba(0, 0, 0, 0.87);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.25;
}

.portfolio-summary__kpi-section-caption {
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  line-height: 1.3;
}

.portfolio-summary__metric {
  position: relative;
  overflow: hidden;
  width: 100%;
  min-height: 158px;
  padding: 16px 16px 40px;
  background: #ffffff;
}

.portfolio-summary__metric--portfolio {
  border-color: rgba(88, 103, 221, 0.28) !important;
  background: linear-gradient(135deg, rgba(88, 103, 221, 0.12), transparent 62%),
    #ffffff !important;
}

.portfolio-summary__metric-topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.portfolio-summary__metric-label {
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 1.2;
  text-transform: uppercase;
}

.portfolio-summary__metric-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  border-radius: 999px;
  background: #f4f6ff;
  color: #5867dd;
  font-size: 18px;
  font-weight: 800;
  line-height: 1;
}

.portfolio-summary__metric-icon span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.portfolio-summary__metric-value {
  margin-top: 10px;
  color: rgba(0, 0, 0, 0.87);
  font-size: 36px;
  font-weight: 800;
  line-height: 1;
}

.portfolio-summary__metric-caption {
  margin-top: 6px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 13px;
  line-height: 1.35;
}

.portfolio-summary__metric-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.portfolio-summary__metric-breakdown-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  background: #ffffff;
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  font-weight: 700;
}

.portfolio-summary__metric-breakdown-chip strong {
  color: rgba(0, 0, 0, 0.87);
  font-size: 13px;
}

.portfolio-summary__split-metric {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 22px;
}

.portfolio-summary__split-metric-item {
  min-width: 0;
}

.portfolio-summary__metric-flow-value {
  overflow: hidden;
  color: rgba(0, 0, 0, 0.87);
  font-size: 20px;
  font-weight: 800;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.portfolio-summary__metric-flow-value--success {
  color: #1bc5bd;
}

.portfolio-summary__metric-flow-label {
  margin-top: 5px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  line-height: 1.25;
}

.portfolio-summary__metric-flow-progress {
  overflow: hidden;
  height: 8px;
  margin-top: 18px;
  border-radius: 999px;
  background: #eef0f6;
}

.portfolio-summary__metric-flow-progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #1bc5bd;
}

.portfolio-summary__metric-flow-note {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  line-height: 1.25;
}

.portfolio-summary__metric-flow-note strong {
  color: rgba(0, 0, 0, 0.87);
}

.portfolio-summary__metric-help {
  position: absolute;
  right: 14px;
  bottom: 10px;
}

.portfolio-summary__metric--clickable {
  cursor: pointer;
  transition: box-shadow 0.18s ease, border-color 0.18s ease,
    transform 0.18s ease;
}

.portfolio-summary__metric--clickable::after {
  content: "Detaya git";
  position: absolute;
  right: 40px;
  bottom: 12px;
  color: #5867dd;
  font-size: 12px;
  font-weight: 700;
}

.portfolio-summary__metric--clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(82, 63, 105, 0.08);
}

.portfolio-summary__metric--active {
  border-color: #5867dd !important;
  box-shadow: 0 0 0 1px rgba(88, 103, 221, 0.12);
}

.portfolio-summary__billing-toolbar-actions {
  align-items: center;
}

.portfolio-summary__billing-group-chip {
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.18s ease, border-color 0.18s ease,
    color 0.18s ease, box-shadow 0.18s ease;
}

.portfolio-summary__billing-group-chip:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(88, 103, 221, 0.12);
}

.portfolio-summary__table-filter-chip-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: #9b9b9b;
  color: #ffffff;
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
}

.portfolio-summary__table-filter-chip--active
  .portfolio-summary__table-filter-chip-count {
  background: #5867dd;
}

.portfolio-summary__billing-placeholder {
  background: #fafbff;
}

.portfolio-summary__year-target {
  width: 180px;
  max-width: 100%;
}

.portfolio-summary__yearly-stat {
  min-height: 88px;
  background: #ffffff;
}

.portfolio-summary__yearly-stat--bridge {
  background: #f7f4ff;
}

.portfolio-summary__yearly-stat--not-ready {
  background: #fffaf2;
}

.portfolio-summary__risk-row--danger {
  background: #fff4f4;
}

.portfolio-summary__risk-row--warning {
  background: #fffaf0;
}

.portfolio-summary__risk-row--soft {
  background: #fafbff;
}

.portfolio-summary__preview-table {
  border: 1px solid rgba(60, 60, 60, 0.12);
  border-radius: 8px;
}

.portfolio-summary__tabs {
  border-bottom: 1px solid rgba(60, 60, 60, 0.14);
}

.portfolio-summary__tab-badge ::v-deep .v-badge__badge {
  min-width: 26px;
  height: 26px;
  padding: 0 8px;
  border-radius: 999px;
  background: #9b9b9b !important;
  color: #ffffff !important;
  font-size: 13px;
  font-weight: 800;
  line-height: 26px;
}

.v-tab--active .portfolio-summary__tab-badge ::v-deep .v-badge__badge {
  background: #5867dd !important;
}

.portfolio-summary__table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.portfolio-summary__table-toolbar-copy {
  min-width: 0;
}

.portfolio-summary__table-toolbar-title {
  color: rgba(0, 0, 0, 0.87);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
}

.portfolio-summary__table-toolbar-subtitle {
  margin-top: 4px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 13px;
  line-height: 1.3;
}

.portfolio-summary__table-toolbar-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.portfolio-summary__table-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 34px;
  padding: 0 14px;
  border: 1px solid rgba(60, 60, 60, 0.16);
  border-radius: 999px;
  background: #ffffff;
  color: rgba(0, 0, 0, 0.62);
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.portfolio-summary__table-filter-chip--active {
  border-color: #5867dd;
  background: #f7f8ff;
  color: #5867dd;
}

.portfolio-summary__data-table ::v-deep tbody > tr > td {
  height: auto;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.portfolio-summary__table-entity,
.portfolio-summary__table-issue,
.portfolio-summary__table-stack {
  min-width: 0;
}

.portfolio-summary__table-entity-primary,
.portfolio-summary__table-issue-primary,
.portfolio-summary__table-stack-primary {
  overflow: hidden;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 600;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.portfolio-summary__table-entity-secondary,
.portfolio-summary__table-issue-secondary,
.portfolio-summary__table-stack-secondary {
  overflow: hidden;
  margin-top: 2px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.portfolio-summary__table-amount {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 700;
  text-align: right;
  white-space: nowrap;
}

@media (max-width: 1263px) {
  .portfolio-summary__operation-items {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .portfolio-summary__operation-item:nth-child(2) {
    border-right: 0;
  }

  .portfolio-summary__operation-item:nth-child(-n + 2) {
    border-bottom: 1px solid rgba(60, 60, 60, 0.12);
  }
}

@media (max-width: 599px) {
  .portfolio-summary__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .portfolio-summary__header-actions {
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .portfolio-summary__table-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .portfolio-summary__table-toolbar-filters {
    justify-content: flex-start;
  }

  .portfolio-summary__operation-items {
    grid-template-columns: 1fr;
  }

  .portfolio-summary__operation-item,
  .portfolio-summary__operation-item:nth-child(2) {
    border-right: 0;
  }

  .portfolio-summary__operation-item:not(:last-child) {
    border-bottom: 1px solid rgba(60, 60, 60, 0.12);
  }
}
</style>
